import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Code,
  Divider,
  Grid,
  Group,
  NumberInput,
  Modal,
  Paper,
  Progress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconAlertTriangle, IconClock, IconDownload, IconLayersSubtract, IconPlayerPause, IconPlayerPlay, IconX } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery } from "@tanstack/react-query";

import { api, ApiClientError } from "../api/client";
import { EmptyState, ErrorState, LoadingState } from "../components/ui";
import { KRONOS_FIELD_DESCRIPTIONS, KRONOS_MODEL_OPTIONS, kronosSignatureFromParams } from "../lib/kronosFields";
import { COUNTERPART_FIELD_DESCRIPTIONS, COUNTERPART_SWEEPABLE_BOOL_KEYS } from "../lib/counterpartFields";
import { FAVORITE_HOLD_FIELD_DESCRIPTIONS, FAVORITE_HOLD_FIELD_LABELS, FAVORITE_HOLD_SWEEPABLE_BOOL_KEYS } from "../lib/favoriteHoldFields";
import { candleTimeframeOptionsFor } from "../lib/candleTimeframes";
import { FIELD_COPY } from "../lib/featureCopy";
import {
  FeatureCardShell,
  FeatureColumnToolbar,
  FeatureGroupHeader,
  VALUE_FEATURE_DEFAULTS,
  isSectionActive,
  isSectionRemovable,
  useCollapsedPanels,
  useSortedSections,
  valueFieldFor,
  type FeatureSectionLike,
} from "../components/FeatureCards";
import { KronosCachePicker } from "../components/KronosCachePicker";
import type { KronosSignatureInfo } from "../api/kronosSchemas";
import { useMarket } from "../hooks/useMarket";
import { monoStyle } from "../theme";

// Mirrors ManualSimulator.tsx FEATURE_GROUPS so the sweep form exposes the
// exact same configurable surface area. Keys that should never be swept
// (config_name, log_*, etc.) are filtered through HIDDEN_FIELDS below.
const HIDDEN_FIELDS = new Set([
  "config_name",
  "display_timezone",
  "dry_run",
  "log_level",
  "log_file",
  "state_file",
  "monitor_after_decision",
  "monitor_interval_seconds",
]);

// Fields rendered as a single TextInput (no Min/Max/Step) per spec:
// the Cascade Wins ("cascading_sizes") and the Time Range
// ("trade_time_ranges") configurations don't compose into a numeric
// range sweep.
const SINGLE_TEXT_FIELDS = new Set(["cascading_sizes", "trade_time_ranges", "trade_days_allowed"]);

// Fields that support a bracket-list-of-lists sweep syntax:
//   "MON,TUE,WED"                → fixed single value (no axis)
//   "[MON,TUE,WED],[THU,FRI,SAT]" → axis with two values, one per bracketed set
// See `parseBracketSetSweep`.
const BRACKET_SET_SWEEP_FIELDS = new Set(["trade_days_allowed"]);

/**
 * Parse a bracket-list-of-lists sweep expression.
 *
 * Accepted forms:
 *   - Plain (no brackets): returned as a single fixed value with the raw
 *     string preserved (whitespace-trimmed at edges only).
 *   - One or more `[...]` groups separated by commas/whitespace: each group's
 *     inner CSV is returned as a distinct axis value.
 *
 * Returns `null` when brackets are present but malformed (unbalanced, empty
 * groups, or stray content outside groups) so the caller can surface an
 * inline error.
 */
function parseBracketSetSweep(
  raw: string,
): { kind: "fixed"; value: string } | { kind: "axis"; values: string[] } | null {
  const trimmed = (raw ?? "").trim();
  if (!trimmed.includes("[") && !trimmed.includes("]")) {
    return { kind: "fixed", value: trimmed };
  }
  const groups: string[] = [];
  let i = 0;
  while (i < trimmed.length) {
    const ch = trimmed[i];
    if (ch === " " || ch === "," || ch === "\t") {
      i += 1;
      continue;
    }
    if (ch !== "[") return null;
    const close = trimmed.indexOf("]", i + 1);
    if (close < 0) return null;
    const inner = trimmed.slice(i + 1, close).trim();
    if (!inner) return null;
    groups.push(inner);
    i = close + 1;
  }
  if (groups.length === 0) return null;
  if (groups.length === 1) return { kind: "fixed", value: groups[0]! };
  return { kind: "axis", values: groups };
}
// Fields that must always be treated as decimals even when their current
// template value happens to be an integer (e.g. 0). Without this, a
// template value of 0 passes templateIsInteger() → coerceNumeric truncates
// decimal inputs (0.1 → 0) and the range collapses to a fixed 0.
export const DECIMAL_FIELDS = new Set([
  "trade_size_usdc",
  "min_odds",
  "max_odds",
  "loss_streak_continue_threshold_usdc",
  "late_buy_trade_size_usdc",
  "open_price_cushion_min_bps",
  "stop_loss_btc_delta_dollars",
  "stop_loss_btc_poll_interval_seconds",
  "momentum_sell_window_value",
  "pre_decision_avg_odds_min",
  "momentum_sell_sl_threshold_value",
  "momentum_sell_tp_threshold_value",
  "odds_momentum_avg_max_diff",
  "take_profit_threshold",
  "stop_loss_threshold",
  "compounding_win_pct",
  "compounding_loss_pct",
  "compounding_max_size_usdc",
  "pre_order_up_avg_odds_min",
  "pre_order_up_avg_odds_max",
  "pre_order_down_avg_odds_min",
  "pre_order_down_avg_odds_max",
  "pre_order_up_min_odds_min",
  "pre_order_up_min_odds_max",
  "pre_order_up_min_pct_min",
  "pre_order_up_min_pct_max",
  "pre_order_down_min_odds_min",
  "pre_order_down_min_odds_max",
  "pre_order_down_max_pct_min",
  "pre_order_down_max_pct_max",
  // Kronos confidence floor is swept and can be a whole value like 1.0 in a
  // template, which templateIsInteger() would treat as an int and truncate a
  // swept 0.1 step down to 0. Force decimal handling.
  "kronos_min_confidence",
  "counterpart_odds_threshold",
  "counterpart_decide_within_pct",
  "favorite_hold_flip_threshold",
  "favorite_hold_tier1_min_odds",
  "favorite_hold_tier2_min_rise",
  // Hedge-at-entry percent defaults to 0 (int) but sweeps like 12.5 must
  // not be truncated.
  "hedge_pct",
  // Entry-quality gates / Tier-2 exit breach: float thresholds whose
  // template defaults (0, 1.5, 0.06...) must sweep as decimals.
  "max_drop_from_peak_odds",
  "min_price_excursion_retention",
  "vol_cushion_min_z",
  "underlying_velocity_min_bps",
  "max_spread",
  "counterpart_confirm_max_opposing_bps",
  "kronos_min_edge_over_ask",
  "exit_underlying_breach_bps",
]);

// Entry-quality gates + Tier-2 exit controls (trading_bot/entry_quality.py
// defaults). Backfilled into templates / saved sweeps so older snapshots
// replay byte-identical with every gate disabled.
export const ENTRY_QUALITY_DEFAULTS: Record<string, unknown> = {
  peak_drawdown_gate_enabled: false,
  max_drop_from_peak_odds: 0.06,
  min_price_excursion_retention: 0,
  vol_cushion_gate_enabled: false,
  vol_cushion_min_z: 1.5,
  underlying_velocity_gate_enabled: false,
  underlying_velocity_lookback_seconds: 15,
  underlying_velocity_min_bps: 0,
  open_side_gate_enabled: false,
  open_side_streak_seconds: 60,
  max_open_crosses: 0,
  max_spread_gate_enabled: false,
  max_spread: 0.03,
  counterpart_confirm_gate_enabled: false,
  counterpart_confirm_mode: "price",
  counterpart_confirm_max_opposing_bps: 0,
  kronos_min_edge_over_ask: 0,
  exit_require_underlying_breach: false,
  exit_underlying_breach_bps: 0,
  exit_order_type: "INHERIT",
  exit_slippage_ticks: -1,
};

const FEATURE_GROUPS: Array<{ title: string; description: string; enabledKey?: string; metaKey?: string; keys: string[] }> = [
  {
    title: "Config & Core Entry",
    description: "Which config version, date range and execution target to sweep, plus the trade itself: size, the odds band the favourite must sit in, when the first decision is evaluated, and the hard no-buy / no-sell cutoffs near close. Every other card below either filters this trade set (gates), extends it (overrides) or changes how it exits.",
    metaKey: "core",
    keys: [
      "trade_size_usdc",
      "min_odds",
      "max_odds",
      "decision_time_seconds",
      "no_buy_window_seconds",
      "no_sell_window_seconds",
    ],
  },
  {
    title: "Candle Filter",
    description: "Require the last N closed candles (of the chosen timeframe) to agree with the picked direction before entering. Turning this OFF bypasses the gate exactly like Min Matching Candles = 0. Sweep Min Matching Candles / Candles To Check to find the agreement depth that pays.",
    enabledKey: "candle_filter_enabled",
    keys: [
      "candle_timeframe_seconds",
      "skip_first_candles",
      "min_matching_candles",
      "candles_to_check",
      "allow_live_candle",
      "live_candle_minute",
    ],
  },
  {
    title: "Entry Confirmation Delay",
    description: "Live + Sim: once every entry gate passes, keep re-checking for N consecutive seconds in the same direction before placing the order. Sweep 0-5 to see how much a short confirmation costs in volume vs. gains in accuracy. 0 = off.",
    metaKey: "entry_confirm",
    keys: ["entry_confirm_seconds"],
  },
  {
    title: "Simulated Order Placement Delay",
    description: "Sim-only: models the 1–5 s sign + POST latency between the signal and the exchange. The fill happens N seconds later at that tick's ask, and becomes a no-fill if the ask drifted above Max Odds or into the no-buy window. Use 2-3 s for an honest live estimate. 0 = instant fill (optimistic).",
    metaKey: "sim_execution_delay",
    keys: ["sim_execution_delay_seconds"],
  },
  {
    title: "Multiple Orders per Window",
    description: "Live-only behaviour: when ON the bot may re-enter the same window after a position was bought and sold. The simulators always cap at one order per window, so this does not change sweep results — keep it OFF for parity.",
    enabledKey: "allow_multiple_orders_per_window",
    keys: [],
  },
  {
    title: "Win-streak Max Loss Threshold",
    description: "A resolved loss smaller than this many USDC does not break the win streak or reset compounding — it is treated as neutral, like a no-fill. Only matters with cascading / compounding / win-streak skip enabled. 0 = any loss breaks the streak.",
    metaKey: "loss_streak_threshold",
    keys: ["loss_streak_continue_threshold_usdc"],
  },
  {
    title: "Late Buy",
    description: "Allow entry logic near close when candle checks are weaker.",
    enabledKey: "late_buy_enabled",
    keys: ["late_buy_within_seconds", "late_buy_trade_size_usdc"],
  },
  {
    title: "Open-price Direction Gate",
    description: "Require the current price to be on the picked side of the window's open price (UP: current above open; DOWN: current below open). Uses the feed's own price series so it works for any market. Cold-start fails closed.",
    enabledKey: "open_price_gate_enabled",
    keys: [],
  },
  {
    title: "Open-price Candle Override",
    description: "When all other checks pass except the candle filter, still enter if the current price is on the correct side of the window open. Set within seconds to 0 for right away, or a positive value (sweepable) to only trigger in the last X seconds.",
    enabledKey: "open_price_override_enabled",
    keys: ["open_price_override_within_seconds"],
  },
  {
    title: "Open-price Cushion Gate",
    description: "Require the current price to be at least N basis points beyond the window open in the picked direction (UP: above open; DOWN: below open). Rejects at-the-money / wrong-side entries. Threshold is in bps of the open (sweepable), so BTC and ETH share one setting. Cold-start fails closed.",
    enabledKey: "open_price_cushion_enabled",
    keys: ["open_price_cushion_min_bps"],
  },
  {
    title: "Take Profit",
    description: "Exit early if implied probability reaches the target threshold.",
    enabledKey: "take_profit_enabled",
    keys: ["take_profit_threshold"],
  },
  {
    title: "Stop Loss",
    description: "Exit early when price action moves against the position.",
    enabledKey: "stop_loss_enabled",
    keys: [
      "stop_loss_threshold",
      "stop_loss_trigger_within_seconds",
      "stop_loss_btc_delta_enabled",
      "stop_loss_btc_delta_dollars",
      "stop_loss_btc_poll_interval_seconds",
      "stop_loss_btc_delta_mode",
      "stop_loss_btc_confirm_seconds",
      "stop_loss_last_candle_instant",
      "stop_loss_last_candle_seconds",
    ],
  },
  {
    title: "Underlying-Confirmed Protective Exits (Smart SL)",
    description: "Only let the odds Stop Loss and the Momentum Sell SL fire once the reference price has actually given back its cushion: the move from the window open toward the position must be <= N bps (0 = at/past the open). Prevents panic-selling a winning position during brief CLOB quote pulls; take-profit exits are unaffected. Identical in live and simulation.",
    enabledKey: "exit_require_underlying_breach",
    keys: ["exit_underlying_breach_bps"],
  },
  {
    title: "Momentum Sell",
    description: "Exit early via a rolling average of post-fill ask prices. SL sub-mode sells when the average falls below fill ask by a threshold; TP sub-mode sells after the average first peaks above a profit threshold then falls back below it.",
    enabledKey: "momentum_sell_enabled",
    keys: [
      "momentum_sell_window_type",
      "momentum_sell_window_value",
      "momentum_sell_sl_enabled",
      "momentum_sell_sl_threshold_type",
      "momentum_sell_sl_threshold_value",
      "momentum_sell_sl_require_seconds",
      "momentum_sell_sl_start_at_seconds_left",
      "momentum_sell_tp_enabled",
      "momentum_sell_tp_threshold_type",
      "momentum_sell_tp_threshold_value",
      "momentum_sell_tp_min_seconds_below",
      "momentum_sell_tp_start_at_seconds_left",
      "momentum_sell_profit_is_win",
      "momentum_sell_loss_breaks_streak",
    ],
  },
  {
    title: "Skip After Wins",
    description: "Pause entries after a win streak and resume by configured rule.",
    enabledKey: "skip_after_wins_enabled",
    keys: ["skip_after_wins_threshold", "skip_after_wins_losses_to_resume"],
  },
  {
    title: "Cascading Sizes",
    description: "Use staged position sizes instead of a fixed trade size.",
    enabledKey: "cascading_sizes_enabled",
    keys: ["cascading_sizes"],
  },
  {
    title: "Compounding Sizing",
    description: "Grow order size during a win streak by a percent of each win's profit; reset to base after any loss.",
    enabledKey: "compounding_enabled",
    keys: ["compounding_win_pct", "compounding_max_size_usdc", "compounding_stop_after_wins", "compounding_stop_size_stay", "compounding_resume_from_saved"],
  },
  {
    title: "Trade Time Ranges",
    description: "Restrict simulated entries to configured local-time ranges.",
    enabledKey: "trade_time_ranges_enabled",
    keys: ["trade_time_ranges"],
  },
  {
    title: "Trade Days",
    description: "Restrict simulated entries to selected weekdays (in display timezone). Use MON,TUE,WED,... for a single set, or [MON,TUE,WED,SUN],[THU,FRI,SAT] to sweep multiple day-sets in one run.",
    enabledKey: "trade_days_enabled",
    keys: ["trade_days_allowed"],
  },
  {
    title: "Momentum",
    description: "Require recent odds trend progression before allowing entry.",
    enabledKey: "odds_momentum_enabled",
    keys: [
      "odds_momentum_use_unique_odds",
      "odds_momentum_lookback",
      "odds_momentum_required_increasing",
      "odds_momentum_ignore_last_seconds",
      "odds_momentum_avg_lookback",
      "odds_momentum_avg_max_diff",
    ],
  },
  {
    title: "Pre-order Metrics Filter",
    description: "Rolling per-window averages + extremes of odds and BTC \u0394% observed prior to fill. Each of the 6 filters can block placement when its value falls outside [min, max]. Avg-odds bands are separate per direction because UP/DOWN averages usually differ substantially.",
    enabledKey: "pre_order_metrics_enabled",
    keys: [
      "pre_order_up_avg_odds_filter_enabled",
      "pre_order_up_avg_odds_min",
      "pre_order_up_avg_odds_max",
      "pre_order_down_avg_odds_filter_enabled",
      "pre_order_down_avg_odds_min",
      "pre_order_down_avg_odds_max",
      "pre_order_up_min_odds_filter_enabled",
      "pre_order_up_min_odds_min",
      "pre_order_up_min_odds_max",
      "pre_order_up_min_pct_filter_enabled",
      "pre_order_up_min_pct_min",
      "pre_order_up_min_pct_max",
      "pre_order_down_min_odds_filter_enabled",
      "pre_order_down_min_odds_min",
      "pre_order_down_min_odds_max",
      "pre_order_down_max_pct_filter_enabled",
      "pre_order_down_max_pct_min",
      "pre_order_down_max_pct_max",
    ],
  },
  {
    title: "Pre-decision Average Odds",
    description: "Require the picked side's average mid odds from window open strictly before configured decision time to meet the minimum. The average freezes at decision time; later monitoring cannot change it. Blocks early entries and missing odds. No counterpart or Favorite Hold bypass. Sweep the minimum, e.g. 0.51-0.60.",
    enabledKey: "pre_decision_avg_odds_enabled",
    keys: ["pre_decision_avg_odds_min"],
  },
  {
    title: "Previous Windows Direction",
    description: "Require the last N resolved market windows (previous candles for this market) to have closed in the same direction as the picked side (e.g. 2 of last 3 on a 5m market). Cold-start fails closed. Sweep values like 3-5 for N and 2-4 for min matches.",
    enabledKey: "prev_windows_check_enabled",
    keys: [
      "prev_windows_count",
      "prev_windows_min_matches",
    ],
  },
  {
    title: "Time in Min \u2194 Max Odds Range Before Decision Time",
    description: "Require the picked side's mid odds to have spent at least N seconds inside the configured min/max odds band before decision time (counted from window start on the 1Hz tick grid). Filters favourites that only just spiked into the band. Sweep min dwell seconds like 5-30 to find the volume/accuracy trade-off.",
    enabledKey: "band_dwell_enabled",
    keys: [
      "band_dwell_min_seconds",
      "band_dwell_require_exceeds_opposite",
    ],
  },
  {
    title: "Max Favorite Flips",
    description: "Count how many times the picked side's mid odds dropped below the flip threshold since the warm-up skip (1Hz), and block the entry when that count exceeds the maximum. Hard gate (no counterpart / Favorite Hold bypass). Sweep max flips 0-3 and the skip seconds.",
    enabledKey: "flip_gate_enabled",
    keys: [
      "flip_gate_max_flips",
      "flip_gate_skip_seconds",
      "flip_gate_flip_threshold",
    ],
  },
  {
    title: "Peak Drawdown (Anti Falling-Knife)",
    description: "Block entries where the picked side is falling back INTO the odds band from above: the side's mid must not be more than X below its window peak, and the underlying must still retain at least R of its peak favourable move from the window open. Hard gate (no counterpart / Favorite Hold bypass). Sweep max drop 0.02-0.10 and retention 0.3-0.8.",
    enabledKey: "peak_drawdown_gate_enabled",
    keys: ["max_drop_from_peak_odds", "min_price_excursion_retention"],
  },
  {
    title: "Volatility-Normalised Cushion (z-score)",
    description: "Require z = (current - open) / (realised 1s volatility x sqrt(secs_left)) >= Z in the picked direction. Scales the required cushion with how choppy the window is and how much time is left. Hard gate. Sweep Z 1.0-3.0.",
    enabledKey: "vol_cushion_gate_enabled",
    keys: ["vol_cushion_min_z"],
  },
  {
    title: "Underlying Velocity (last K seconds)",
    description: "Require the reference price to have moved toward the picked direction by at least N bps over the last K seconds (0 bps = merely not moving against you). Hard gate. Sweep lookback 5-30s.",
    enabledKey: "underlying_velocity_gate_enabled",
    keys: ["underlying_velocity_lookback_seconds", "underlying_velocity_min_bps"],
  },
  {
    title: "Open-Side Streak / Open Crossings",
    description: "Require the reference price to have stayed continuously on the picked side of the window open for the last N seconds, and/or block windows where it crossed the open more than K times. 0 disables each sub-check. Hard gate.",
    enabledKey: "open_side_gate_enabled",
    keys: ["open_side_streak_seconds", "max_open_crosses"],
  },
  {
    title: "Bid-Ask Spread Cap",
    description: "Require (ask - bid) on the picked side to be <= the cap at entry. Hard gate. Sweep 0.01-0.05.",
    enabledKey: "max_spread_gate_enabled",
    keys: ["max_spread"],
  },
  {
    title: "Counterpart Confirmation Gate",
    description: "Filter (not override): block the entry when the time-aligned counterpart market (BTC \u2194 ETH, same timeframe) disagrees. 'price' mode blocks when the counterpart's underlying has moved AGAINST the picked direction by more than N bps from its open; 'favorite' mode blocks when the counterpart's favourite side is opposite. Fails closed when the counterpart window is missing. Loads counterpart ticks automatically.",
    enabledKey: "counterpart_confirm_gate_enabled",
    keys: ["counterpart_confirm_mode", "counterpart_confirm_max_opposing_bps"],
  },
  {
    title: "Kronos Forecast",
    description: "Gate entries on the Kronos foundation-model window forecast. Sweep the direction/confidence knobs freely — they are pure post-filters that reuse the SAME cached forecast. Pick which model to read (a single choice, not a range — it must already be precomputed for this market in Settings → Kronos). The remaining inference params (lookback, timeframe, sampling) come from the base config and form the rest of the cache signature; they are NOT swept here because each unique signature needs its own full precompute and uncached windows fail closed. You CAN sweep decision_time_seconds (Core Entry), but every value in the range must have a precomputed cache for the selected signature that spans your date range — the coverage checklist below blocks Run until it does.",
    enabledKey: "kronos_check_enabled",
    keys: [
      "kronos_require_direction",
      "kronos_min_confidence",
      "kronos_min_edge_over_ask",
      "kronos_model",
    ],
  },
  {
    title: "Counterpart Market Override",
    description: `${COUNTERPART_FIELD_DESCRIPTIONS.counterpart_override_enabled} TIP: keep your real gates enabled in the base config, turn this ON, then set each 'Bypass ...' toggle to Sweep — the run enumerates every on/off combination and ranks them, so you can see exactly which gates are worth letting it bypass. Wired on all markets (strongest effect on 5m/15m).`,
    enabledKey: "counterpart_override_enabled",
    keys: [
      "counterpart_odds_threshold",
      "counterpart_decide_within_pct",
      "counterpart_stable_seconds",
      "counterpart_follow_direction",
      "counterpart_follow_exclusive",
      "counterpart_require_trend",
      "counterpart_bypass_candle",
      "counterpart_bypass_open_price",
      "counterpart_bypass_prev_windows",
      "counterpart_bypass_kronos",
      "counterpart_bypass_momentum",
      "counterpart_bypass_pre_order",
      "counterpart_bypass_time_ranges",
      "counterpart_bypass_min_odds",
      "counterpart_bypass_max_odds",
    ],
  },
  {
    title: "Counterpart Matching Order",
    description: `${COUNTERPART_FIELD_DESCRIPTIONS.counterpart_match_order_enabled} TIP: set the 'require' toggles to Sweep to enumerate which checks to re-impose.`,
    enabledKey: "counterpart_match_order_enabled",
    keys: [
      "counterpart_match_order_require_trend",
      "counterpart_match_order_require_candle",
      "counterpart_match_order_require_open_price",
      "counterpart_match_order_require_prev_windows",
      "counterpart_match_order_require_kronos",
      "counterpart_match_order_require_momentum",
      "counterpart_match_order_require_pre_order",
      "counterpart_match_order_require_time_ranges",
      "counterpart_match_order_require_min_odds",
      "counterpart_match_order_require_max_odds",
    ],
  },
  {
    title: "Favorite Hold (3-Tier)",
    description: `${FAVORITE_HOLD_FIELD_DESCRIPTIONS.favorite_hold_enabled} TIP: set the tier/require/bypass toggles to Sweep to enumerate combinations.`,
    enabledKey: "favorite_hold_enabled",
    keys: [
      "favorite_hold_flip_threshold",
      "favorite_hold_skip_seconds",
      "favorite_hold_tier1_enabled",
      "favorite_hold_tier1_min_odds",
      "favorite_hold_tier1_dwell_seconds",
      "favorite_hold_tier2_enabled",
      "favorite_hold_tier2_min_rise",
      "favorite_hold_tier2_check_seconds",
      "favorite_hold_tier2_require_candle",
      "favorite_hold_tier2_require_open_price",
      "favorite_hold_tier2_require_prev_windows",
      "favorite_hold_tier2_require_kronos",
      "favorite_hold_tier2_require_momentum",
      "favorite_hold_tier2_require_pre_order",
      "favorite_hold_tier2_require_time_ranges",
      "favorite_hold_tier2_require_min_odds",
      "favorite_hold_tier3_enabled",
      "favorite_hold_tier3_bypass_candle",
      "favorite_hold_tier3_bypass_open_price",
      "favorite_hold_tier3_bypass_prev_windows",
      "favorite_hold_tier3_bypass_kronos",
      "favorite_hold_tier3_bypass_momentum",
      "favorite_hold_tier3_bypass_pre_order",
      "favorite_hold_tier3_bypass_time_ranges",
      "favorite_hold_tier3_bypass_min_odds",
    ],
  },
  {
    title: "Hedge at Entry",
    description: "When an order is placed, also buy the OPPOSITE side at the same tick with the configured % of the main trade size. The hedge always rides to window resolution (it is never sold by take-profit / stop-loss / momentum-sell exits, which only close the main leg), capping the loss when the market flips against the entry at the cost of part of the win-side profit. Pure P&L overlay: win/loss counts, streaks, compounding and cascading sizes still follow the main leg only. TIP: sweep the hedge % to find the variance/pnl trade-off.",
    enabledKey: "hedge_enabled",
    keys: [
      "hedge_pct",
    ],
  },
];

const EDITABLE_KEYS = new Set(
  FEATURE_GROUPS.flatMap((group) => [
    ...(group.enabledKey ? [group.enabledKey] : []),
    ...group.keys,
  ]),
);

const SORT_BY_OPTIONS: Array<{ value: SortBy; label: string }> = [
  { value: "pnl", label: "Highest Win Amount" },
  { value: "wins", label: "Most Wins" },
  { value: "losses", label: "Least Losses" },
  { value: "win_rate", label: "Highest Win Ratio" },
];

type SortBy = "pnl" | "wins" | "losses" | "win_rate";
type SimTarget = "auto" | "bullmq" | "modal";

const FINAL_STATUSES = new Set(["succeeded", "failed", "cancelled"]);
// Earliest `market_windows.window_start` currently in prod DB:
// 2026-05-15T18:00:00Z (UTC) == 2026-05-15T14:00 (EDT, datetime-local input)
const DEFAULT_SIM_FROM = "2026-05-15T14:00";
const MAX_TRADE_TIME_RANGES = 300;

interface NumericRange {
  min: string;
  max: string;
  step: string;
}

// One row of the Kronos strict cache-coverage check: for a single swept
// decision_time value, whether a cache exists for the selected signature and
// whether that cache spans the sim's date range.
interface KronosCoverageRow {
  dt: number;
  cached: boolean;
  count: number;
  windowMin: string | null;
  windowMax: string | null;
  lowerGap: boolean;
  upperGap: boolean;
}

interface KronosCoverageResult {
  enabled: boolean;
  blocking: boolean;
  loading: boolean;
  rows: KronosCoverageRow[];
  missing: number[];
  /** Decision times whose sim `From` predates the cache's first window. */
  lowerGapDts: number[];
  /** Decision times whose sim `To` postdates the cache's last window. */
  upperGapDts: number[];
  needsExplicitTo: boolean;
  signature: string;
  /**
   * Range every swept dt's cache is guaranteed to cover: the LATEST of all
   * per-dt window_start_min (requiredFrom) and the EARLIEST of all per-dt
   * window_start_max (requiredTo). Aligning From/To to these clears the gap.
   */
  requiredFrom: string | null;
  requiredTo: string | null;
}

interface FinalistRow {
  combo_index: number;
  settings: Record<string, unknown>;
  combination_variations: number;
  wins: number;
  losses: number;
  take_profits: number;
  stop_losses: number;
  momentum_sells: number;
  momentum_sell_profit_count: number;
  momentum_sell_loss_count: number;
  momentum_sell_profit_pnl_usdc: number;
  momentum_sell_loss_usdc: number;
  total_losses: number;
  take_profit_total_won_usdc: number;
  stop_loss_total_lost_usdc: number;
  momentum_sell_total_pnl_usdc: number;
  no_fill_wins: number;
  no_fill_losses: number;
  skips: number;
  blocked: number;
  no_bet: number;
  unresolved: number;
  trades: number;
  win_rate: number;
  est_pnl_usdc: number;
  fees_usdc: number;
  average_odds: number | null;
}

interface SimulationLogRow {
  ts: string;
  level: "info" | "warn" | "error";
  message: string;
}

interface SimIssueSummary {
  title: string;
  message: string;
  nextStep: string;
  evidence: string[];
}

function statusColor(status: string | null | undefined): string {
  switch (status) {
    case "queued": return "yellow";
    case "running": return "blue";
    case "succeeded": return "green";
    case "failed": return "red";
    case "cancelled": return "gray";
    default: return "gray";
  }
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof ApiClientError) return `${error.code}: ${error.message}`;
  if (error instanceof Error) return error.message;
  return "unknown error";
}

function summarizeSimIssue(args: {
  status: string | null | undefined;
  error: string | null | undefined;
  logs: SimulationLogRow[];
}): SimIssueSummary | null {
  const status = args.status ?? null;
  if (status !== "failed" && status !== "cancelled") return null;

  const error = (args.error ?? "").trim();
  const evidence = args.logs
    .map((row) => row.message)
    .filter((msg) => msg.startsWith("failure_evidence:"))
    .map((msg) => msg.replace(/^failure_evidence:\s*/i, "").trim())
    .filter(Boolean)
    .slice(-3);

  const queueCleared = /queue was cleared|input queue was cleared/i.test(error)
    || evidence.some((line) => /queue was cleared|input queue was cleared/i.test(line));
  if (queueCleared) {
    return {
      title: "Run cancelled by Modal queue clear",
      message: "Modal cleared the function input queue while this run was executing.",
      nextStep: "If you still need this run, start it again or use resume-from-artifacts before discarding.",
      evidence,
    };
  }

  const preempted = /preempt/i.test(error)
    || evidence.some((line) => /preempt/i.test(line));
  if (preempted) {
    return {
      title: "Run failed due to worker preemption",
      message: "Modal preempted one or more workers before the sweep could complete.",
      nextStep: "Retry the run (or resume from artifacts) and keep this run saved if you need the diagnostics.",
      evidence,
    };
  }

  return {
    title: status === "cancelled" ? "Run cancelled" : "Run failed",
    message: error || "Simulation ended without a detailed error message.",
    nextStep: "Review the Simulation Log below before discarding this run.",
    evidence,
  };
}

function toIsoOrUndef(value: string): string | undefined {
  if (!value) return undefined;
  const dt = new Date(value);
  return Number.isFinite(dt.getTime()) ? dt.toISOString() : undefined;
}

function countTradeTimeRanges(raw: string): number {
  return raw
    .split(",")
    .map((piece) => piece.trim())
    .filter((piece) => piece.length > 0)
    .length;
}

function assertTradeTimeRangesMax(raw: string): void {
  const count = countTradeTimeRanges(raw);
  if (count > MAX_TRADE_TIME_RANGES) {
    throw new Error(
      `trade_time_ranges supports at most ${MAX_TRADE_TIME_RANGES} ranges (received ${count})`,
    );
  }
}

function assertSweepTradeTimeRangesMax(sweepPayload: unknown): void {
  if (!sweepPayload || typeof sweepPayload !== "object" || Array.isArray(sweepPayload)) return;
  const sweep = sweepPayload as Record<string, unknown>;
  const fixed = sweep.fixed;
  if (!fixed || typeof fixed !== "object" || Array.isArray(fixed)) return;
  const tradeTimeRanges = (fixed as Record<string, unknown>).trade_time_ranges;
  if (typeof tradeTimeRanges !== "string") return;
  assertTradeTimeRangesMax(tradeTimeRanges);
}

// Per-field descriptions shown below the label in simulator settings panels.
const FIELD_DESCRIPTIONS: Record<string, string> = {
  momentum_sell_window_type: "Fixed seconds: always use this many seconds of ask history. Percentage: use this % of secs_left at fill time.",
  momentum_sell_window_value: "Window size in seconds (number mode) or as % of secs_left at fill (percentage mode). E.g. 10 seconds or 50% of remaining time.",
  momentum_sell_sl_enabled: "When on, fire a sell when the rolling average falls below fill ask by the SL threshold for the required seconds.",
  momentum_sell_sl_threshold_type: "Odds points: avg drops by this many points below fill ask. Percentage: avg drops by this % of fill ask.",
  momentum_sell_sl_threshold_value: "SL threshold value. E.g. 0.05 odds points or 5%. Combined with fill ask to compute the sell level.",
  momentum_sell_sl_require_seconds: "Consecutive seconds the average must stay below the SL threshold before selling. 0 = sell immediately on first qualifying tick.",
  momentum_sell_sl_start_at_seconds_left: "Delay SL evaluation until this many seconds are left in the window. 0 = start as soon as the buffer is full (default). E.g. 15 = SL checks only begin when ≤ 15 seconds remain.",
  momentum_sell_tp_enabled: "When on, activate TP mode: once avg reaches fill_ask + threshold, sell when avg falls back below that level.",
  momentum_sell_tp_threshold_type: "Odds points: avg must rise this many points above fill ask to activate TP. Percentage: avg must rise by this % of fill ask.",
  momentum_sell_tp_threshold_value: "TP threshold value. E.g. 0.05 odds points or 5%. Combined with fill ask to compute the activation level.",
  momentum_sell_tp_min_seconds_below: "After TP activates, seconds the average must stay below the TP threshold before selling. 0 = sell on first tick below threshold.",
  momentum_sell_tp_start_at_seconds_left: "Delay TP evaluation until this many seconds are left in the window. 0 = start as soon as the buffer is full (default). E.g. 15 = TP checks only begin when ≤ 15 seconds remain.",
  momentum_sell_profit_is_win: "When on, a momentum sell that closes in profit counts as a win: extends win streak and continues compounding.",
  momentum_sell_loss_breaks_streak: "When on (default), a loss-side momentum sell breaks the win streak and resets compounding state. Turn off to treat loss-side momentum sells as neutral — preserving the streak and compounding sequence so a defensive SL exit doesn't penalise the active cascade.",
  loss_streak_continue_threshold_usdc: "Max loss amount (USDC) the bot is allowed to take while still continuing the active win streak. Any resolved loss with |P/L| at or above this amount resets the streak; losses strictly below it are treated as neutral. 0 disables — every loss resets the streak. Note: Momentum Sell SL has its own setting (Loss-side sell breaks win streak) that controls whether momentum-sell losses break the streak.",
  allow_multiple_orders_per_window: "When off (default), at most one order is placed per window (matching the simulation engines; manual buys always bypass this gate). When on, the trading bot may enter a new position in the same window after a previous order has been bought and sold.",
  entry_confirm_seconds: "Live + Sim: consecutive seconds all entry gates must remain passing in the same direction before placing the order. 0 = place immediately on first passing tick.",
  sim_execution_delay_seconds: "Sim-only: models the 1–5s live order execution latency after all checks pass. The simulated fill occurs N seconds later at that tick's ask price (and fails as a no-fill if the ask drifted above max_odds or past no_buy_window_seconds). 0 = instant fill.",
  max_drop_from_peak_odds: "Block when the picked side's mid is more than this many odds points (0.0 – 1.0) below the highest mid it reached earlier in this window. 0 disables this sub-check.",
  min_price_excursion_retention: "Require (current move from open) / (peak favourable move from open) >= this fraction. 0 disables this sub-check.",
  vol_cushion_min_z: "Minimum z-score. z = signed (current - open) / (stdev of 1-second price changes so far x sqrt(secs_left)). Typical 1.5–2.5.",
  underlying_velocity_lookback_seconds: "Look back this many seconds for the reference price (uses the newest tick at least K seconds old).",
  underlying_velocity_min_bps: "Minimum signed move over the lookback, in bps toward the picked direction. 0 = must not be moving against the pick; negative values tolerate a small adverse drift.",
  open_side_streak_seconds: "Consecutive 1Hz samples (seconds) the reference price must have stayed strictly on the picked side of the window open, ending at the current tick. 0 disables.",
  max_open_crosses: "Maximum number of times the reference price may have crossed the window open so far this window. 0 disables (1 allows at most one cross).",
  max_spread: "Maximum (ask - bid) on the picked side at the entry tick, in odds (0.02 = 2 cents).",
  counterpart_confirm_mode: "'price': compare the counterpart's underlying move from its open (blocks if opposing by more than the bps cap). 'favorite': require the counterpart's favourite side (higher mid) to match the picked direction. Single choice, not swept.",
  counterpart_confirm_max_opposing_bps: "'price' mode only: largest counterpart move AGAINST the picked direction (bps from the counterpart's open) still tolerated. 0 = any opposing move blocks.",
  kronos_min_edge_over_ask: "Require Kronos P(picked side) >= rounded entry ask + this edge (e.g. 0.03). 0 disables (the plain confidence floor still applies).",
  exit_underlying_breach_bps: "Protective exits (odds SL / Momentum Sell SL) are allowed only when the reference price's signed move from the window open toward the position is <= this many bps. 0 = at or past the open.",
  trade_days_allowed: "Comma-separated MON/TUE/WED/THU/FRI/SAT/SUN. Sweep multiple day-sets by wrapping each set in brackets, e.g. [MON,TUE,WED,SUN],[THU,FRI,SAT] runs two simulations — one for each set.",
  open_price_cushion_min_bps: "Minimum cushion (basis points of the window open) the current price must be BEYOND the open in the bet direction before entering. e.g. 2 = 0.02% (~$15 on a $77k BTC window, ~$0.48 on a $2.4k ETH window). Applies only when the Cushion Gate is on.",
  ...KRONOS_FIELD_DESCRIPTIONS,
  ...COUNTERPART_FIELD_DESCRIPTIONS,
  ...FAVORITE_HOLD_FIELD_DESCRIPTIONS,
  // Plain-English copy wins over the older technical text above.
  ...FIELD_COPY,
};

// Boolean bypass toggles that can be set to "Sweep" (test both off & on) in
// the sweep form so the run enumerates every combination of the gates you're
// willing to let the counterpart override bypass.
const SWEEPABLE_BOOL_KEYS = new Set<string>([
  ...COUNTERPART_SWEEPABLE_BOOL_KEYS,
  ...FAVORITE_HOLD_SWEEPABLE_BOOL_KEYS,
  "band_dwell_require_exceeds_opposite",
]);

// Friendly labels for keys whose snake-case default is unclear.
const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  compounding_stop_after_wins: "Stop Compounding After X Wins",
  compounding_stop_size_stay: "Keep compounded size after stop",
  compounding_resume_from_saved: "Resume from saved size after loss",
  allow_multiple_orders_per_window: "Allow Multiple Orders per Window",
  entry_confirm_seconds: "Entry Confirmation Delay (seconds)",
  sim_execution_delay_seconds: "Simulated Order Placement Delay (seconds)",
  max_drop_from_peak_odds: "Max Drop From Peak Odds",
  min_price_excursion_retention: "Min Price Excursion Retention (0.0 – 1.0)",
  vol_cushion_min_z: "Min Cushion z-score",
  underlying_velocity_lookback_seconds: "Velocity Lookback (seconds)",
  underlying_velocity_min_bps: "Min Velocity (bps toward pick)",
  open_side_streak_seconds: "Open-Side Streak (seconds)",
  max_open_crosses: "Max Open Crossings",
  max_spread: "Max Bid-Ask Spread",
  counterpart_confirm_mode: "Confirmation Mode",
  counterpart_confirm_max_opposing_bps: "Max Opposing Counterpart Move (bps)",
  kronos_min_edge_over_ask: "Min Edge Over Entry Ask",
  exit_underlying_breach_bps: "Breach Threshold (bps from open)",
  momentum_sell_window_type: "Window Type",
  momentum_sell_window_value: "Window Size",
  momentum_sell_sl_enabled: "Enable SL Sub-mode",
  momentum_sell_sl_threshold_type: "SL Threshold Type",
  momentum_sell_sl_threshold_value: "SL Threshold Value",
  momentum_sell_sl_require_seconds: "SL Require Seconds",
  momentum_sell_sl_start_at_seconds_left: "SL Start at Seconds Left",
  momentum_sell_tp_enabled: "Enable TP Sub-mode",
  momentum_sell_tp_threshold_type: "TP Threshold Type",
  momentum_sell_tp_threshold_value: "TP Threshold Value",
  momentum_sell_tp_min_seconds_below: "TP Min Seconds Below",
  momentum_sell_tp_start_at_seconds_left: "TP Start at Seconds Left",
  momentum_sell_profit_is_win: "Count profitable sells as wins",
  momentum_sell_loss_breaks_streak: "Loss-side sell breaks win streak",
  loss_streak_continue_threshold_usdc: "Win Streak Max Loss Threshold (USDC)",
  prev_windows_count: "Windows to Check (N)",
  prev_windows_min_matches: "Required Matches (M)",
  open_price_cushion_min_bps: "Min Cushion (bps of open)",
  pre_decision_avg_odds_min: "Min Frozen Average Odds (0.0 – 1.0)",
  band_dwell_min_seconds: "Min Time in Range (seconds)",
  band_dwell_require_exceeds_opposite: "Require Time in Range > Opposite Side",
  flip_gate_max_flips: "Max Flips",
  flip_gate_skip_seconds: "Skip First (seconds)",
  flip_gate_flip_threshold: "Flip Threshold (0.0 – 1.0)",
  kronos_require_direction: "Require Direction Match",
  kronos_min_confidence: "Min Confidence (0.0 – 1.0)",
  ...FAVORITE_HOLD_FIELD_LABELS,
};

function formatFieldLabel(key: string): string {
  const override = FIELD_LABEL_OVERRIDES[key];
  if (override) return override;
  return key.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function buildManualSimulatorImportUrl(args: {
  settings: Record<string, unknown>;
  from: string;
  to: string;
}): string {
  const base = `${window.location.origin}${window.location.pathname}`;
  const params = new URLSearchParams({
    import_settings: JSON.stringify(args.settings),
  });
  if (args.from.trim()) params.set("import_from", args.from);
  if (args.to.trim()) params.set("import_to", args.to);
  return `${base}#/analytics/manual-simulator?${params.toString()}`;
}

function valueToInput(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try { return JSON.stringify(value); } catch { return ""; }
}

function isNumericTemplate(value: unknown): boolean {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string") {
    const n = Number(value);
    return value.trim() !== "" && Number.isFinite(n);
  }
  return false;
}

function templateIsInteger(value: unknown): boolean {
  if (typeof value === "number") return Number.isInteger(value);
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) && Number.isInteger(n) && !value.includes(".");
  }
  return false;
}

function coerceNumeric(raw: string, asInteger: boolean): number | null {
  if (raw.trim() === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return asInteger ? Math.trunc(n) : n;
}

function defaultRangeFromMin(minRaw: string): { max: string; step: string } {
  const trimmed = minRaw.trim();
  const numeric = Number(trimmed);
  const isFiniteNumeric = trimmed !== "" && Number.isFinite(numeric);
  const isFloat = isFiniteNumeric && trimmed.includes(".");
  const step = isFloat ? "0.1" : "1";
  if (!isFiniteNumeric) return { max: minRaw, step };

  if (!isFloat) {
    return { max: String(numeric), step };
  }

  const precision = Math.max(1, decimalCount(trimmed));
  return {
    max: numeric.toFixed(precision),
    step,
  };
}

/**
 * Enumerate axis values from a Min/Max/Step triple.
 * - Empty/invalid step or max → fixed value (just min) returned as single.
 * - If min == max → single value.
 * - Decimal-typed fields preserve the user-typed precision by formatting
 *   each emitted value via the larger of min/step decimal counts.
 */
export function enumerateAxisValues(
  range: NumericRange,
  template: unknown,
  forceDecimal = false,
): { kind: "fixed"; value: unknown } | { kind: "axis"; values: unknown[] } {
  const asInt = forceDecimal ? false : templateIsInteger(template);
  const min = coerceNumeric(range.min, asInt);
  if (min == null) {
    // No min entered — fall back to original template value verbatim.
    return { kind: "fixed", value: template };
  }
  const max = coerceNumeric(range.max, asInt);
  const step = coerceNumeric(range.step, asInt);
  if (max == null || step == null || step === 0 || max <= min) {
    return { kind: "fixed", value: asInt ? min : encodeNumeric(min, range, template) };
  }
  const decimals = Math.max(decimalCount(range.min), decimalCount(range.step), decimalCount(range.max));
  const values: unknown[] = [];
  for (let v = min; v <= max + 1e-9; v += step) {
    const rounded = asInt ? Math.round(v) : Number(v.toFixed(decimals));
    values.push(asInt ? rounded : formatDecimal(rounded, decimals, template));
  }
  // Dedupe by string equality while preserving first-occurrence order so
  // the rendered axis matches the input sequence.
  const seen = new Set<string>();
  const unique: typeof values = [];
  for (const v of values) {
    const k = String(v);
    if (seen.has(k)) continue;
    seen.add(k);
    unique.push(v);
  }
  if (unique.length <= 1) {
    return { kind: "fixed", value: asInt ? min : encodeNumeric(min, range, template) };
  }
  return { kind: "axis", values: unique };
}

function decimalCount(raw: string): number {
  const idx = raw.indexOf(".");
  if (idx === -1) return 0;
  return raw.length - idx - 1;
}

function formatDecimal(value: number, decimals: number, template: unknown): unknown {
  if (typeof template === "number") return Number(value.toFixed(decimals));
  return value.toFixed(decimals);
}

function encodeNumeric(value: number, range: NumericRange, template: unknown): unknown {
  if (typeof template === "number") return value;
  const decimals = decimalCount(range.min);
  return decimals > 0 ? value.toFixed(decimals) : String(value);
}

// --------------------------------------------------------------------------
// Load-config import: reconstruct the Automated Simulator form from a saved
// sweep form deep-linked via the URL hash query (see
// `buildAutomatedSimulatorImportUrl` in SavedSimulations.tsx). The inbound
// payload is the exact `sweep` object this page emits, so reconstruction is
// the inverse of the `sweepPayload` builder below.
// --------------------------------------------------------------------------

interface ImportedSweep {
  sweep: Record<string, unknown>;
  subtractNoFills: boolean;
  excludeStaleOdds: boolean;
}

function parseImportedSweepFromUrl(): ImportedSweep | null {
  const hash = window.location.hash || "";
  const qIdx = hash.indexOf("?");
  if (qIdx < 0) return null;
  const params = new URLSearchParams(hash.slice(qIdx + 1));
  const raw = params.get("import_sweep");
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    return {
      sweep: parsed as Record<string, unknown>,
      subtractNoFills: params.get("import_subtract_no_fills") === "1",
      excludeStaleOdds: params.get("import_exclude_stale_odds") === "1",
    };
  } catch {
    return null;
  }
}

function toLocalDateTimeInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const dt = new Date(iso);
  if (!Number.isFinite(dt.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

// Invert a numeric axis (ascending arithmetic sequence emitted by
// `enumerateAxisValues`) back into a Min/Max/Step triple. Uniform sequences
// round-trip exactly; the decimal precision is inferred from the widest
// value so decimal-typed axes reproduce their original strings.
function deriveRangeFromAxisValues(values: unknown[]): NumericRange {
  const nums = values.map((v) => Number(v)).filter((n) => Number.isFinite(n));
  if (nums.length === 0) return { min: "", max: "", step: "1" };
  const decimals = values.reduce<number>((acc, v) => Math.max(acc, decimalCount(String(v))), 0);
  const fmt = (n: number) => (decimals > 0 ? n.toFixed(decimals) : String(Math.round(n)));
  const min = nums[0]!;
  const max = nums[nums.length - 1]!;
  const step = nums.length >= 2
    ? nums[1]! - nums[0]!
    : (decimals > 0 ? Math.pow(10, -decimals) : 1);
  return { min: fmt(min), max: fmt(max), step: fmt(step) };
}

interface ReconstructedSweepState {
  configTemplate: Record<string, unknown>;
  singleInputs: Record<string, string>;
  rangeInputs: Record<string, NumericRange>;
  candleTfSelected: number[];
  from: string;
  to: string;
  sortBy: SortBy;
  topResults: number;
  minWins: number;
  minWinRate: number;
  maxLosses: number | "";
  bestPerLossCount: boolean;
  fastScreenEnabled: boolean;
  fastScreenEta: number;
  fastScreenMinWindows: number;
  fastScreenMaxKeep: number;
  fastScreenEarlyDedup: boolean;
}

export function reconstructSweepFormState(sweep: Record<string, unknown>): ReconstructedSweepState {
  const fixed = (sweep.fixed && typeof sweep.fixed === "object" && !Array.isArray(sweep.fixed))
    ? { ...(sweep.fixed as Record<string, unknown>) }
    : {};

  const axesRaw = Array.isArray(sweep.axes) ? sweep.axes : [];
  const axisByKey = new Map<string, unknown[]>();
  for (const axis of axesRaw) {
    if (!axis || typeof axis !== "object" || Array.isArray(axis)) continue;
    const key = String((axis as Record<string, unknown>).key ?? "");
    const values = (axis as Record<string, unknown>).values;
    if (key && Array.isArray(values) && values.length > 0) axisByKey.set(key, values);
  }

  // Template = fixed values verbatim + each axis key seeded with its first
  // swept value so the single-vs-range bucketing and numeric coercion match
  // the original form exactly.
  const configTemplate: Record<string, unknown> = { ...fixed };
  for (const [key, values] of axisByKey) configTemplate[key] = values[0];

  // Backfill fields added AFTER an older saved sweep was persisted. The
  // sim-bot requires every REQUIRED_CONFIG_FIELD to be covered by the
  // form's fixed+axes, so a reconstructed template missing a newer field
  // would fail with "sweep form does not produce full snapshots".
  // Defaults are the disabled/no-op shape so replay stays byte-identical.
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "open_price_gate_enabled")) configTemplate.open_price_gate_enabled = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "open_price_override_enabled")) configTemplate.open_price_override_enabled = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "open_price_override_within_seconds")) configTemplate.open_price_override_within_seconds = 0;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "open_price_cushion_enabled")) configTemplate.open_price_cushion_enabled = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "open_price_cushion_min_bps")) configTemplate.open_price_cushion_min_bps = 2.0;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_override_enabled")) configTemplate.counterpart_override_enabled = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_odds_threshold")) configTemplate.counterpart_odds_threshold = 0.95;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_decide_within_pct")) configTemplate.counterpart_decide_within_pct = 0;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_stable_seconds")) configTemplate.counterpart_stable_seconds = 0;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_require_trend")) configTemplate.counterpart_require_trend = true;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_follow_direction")) configTemplate.counterpart_follow_direction = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_follow_exclusive")) configTemplate.counterpart_follow_exclusive = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_bypass_candle")) configTemplate.counterpart_bypass_candle = true;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_bypass_open_price")) configTemplate.counterpart_bypass_open_price = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_bypass_prev_windows")) configTemplate.counterpart_bypass_prev_windows = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_bypass_kronos")) configTemplate.counterpart_bypass_kronos = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_bypass_momentum")) configTemplate.counterpart_bypass_momentum = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_bypass_pre_order")) configTemplate.counterpart_bypass_pre_order = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_bypass_time_ranges")) configTemplate.counterpart_bypass_time_ranges = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_bypass_min_odds")) configTemplate.counterpart_bypass_min_odds = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_bypass_max_odds")) configTemplate.counterpart_bypass_max_odds = false;
  // Counterpart matching-order (sub-feature): disabled / no-op defaults.
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_match_order_enabled")) configTemplate.counterpart_match_order_enabled = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_match_order_require_trend")) configTemplate.counterpart_match_order_require_trend = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_match_order_require_candle")) configTemplate.counterpart_match_order_require_candle = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_match_order_require_open_price")) configTemplate.counterpart_match_order_require_open_price = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_match_order_require_prev_windows")) configTemplate.counterpart_match_order_require_prev_windows = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_match_order_require_kronos")) configTemplate.counterpart_match_order_require_kronos = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_match_order_require_momentum")) configTemplate.counterpart_match_order_require_momentum = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_match_order_require_pre_order")) configTemplate.counterpart_match_order_require_pre_order = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_match_order_require_time_ranges")) configTemplate.counterpart_match_order_require_time_ranges = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_match_order_require_min_odds")) configTemplate.counterpart_match_order_require_min_odds = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "counterpart_match_order_require_max_odds")) configTemplate.counterpart_match_order_require_max_odds = false;

  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_enabled")) configTemplate.favorite_hold_enabled = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_flip_threshold")) configTemplate.favorite_hold_flip_threshold = 0.51;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_skip_seconds")) configTemplate.favorite_hold_skip_seconds = 0;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier1_enabled")) configTemplate.favorite_hold_tier1_enabled = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier1_min_odds")) configTemplate.favorite_hold_tier1_min_odds = 0.8;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier1_dwell_seconds")) configTemplate.favorite_hold_tier1_dwell_seconds = 60;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier2_enabled")) configTemplate.favorite_hold_tier2_enabled = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier2_min_rise")) configTemplate.favorite_hold_tier2_min_rise = 0.1;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier2_check_seconds")) configTemplate.favorite_hold_tier2_check_seconds = 120;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier2_require_candle")) configTemplate.favorite_hold_tier2_require_candle = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier2_require_open_price")) configTemplate.favorite_hold_tier2_require_open_price = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier2_require_prev_windows")) configTemplate.favorite_hold_tier2_require_prev_windows = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier2_require_kronos")) configTemplate.favorite_hold_tier2_require_kronos = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier2_require_momentum")) configTemplate.favorite_hold_tier2_require_momentum = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier2_require_pre_order")) configTemplate.favorite_hold_tier2_require_pre_order = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier2_require_time_ranges")) configTemplate.favorite_hold_tier2_require_time_ranges = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier2_require_min_odds")) configTemplate.favorite_hold_tier2_require_min_odds = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier3_enabled")) configTemplate.favorite_hold_tier3_enabled = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier3_bypass_candle")) configTemplate.favorite_hold_tier3_bypass_candle = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier3_bypass_open_price")) configTemplate.favorite_hold_tier3_bypass_open_price = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier3_bypass_prev_windows")) configTemplate.favorite_hold_tier3_bypass_prev_windows = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier3_bypass_kronos")) configTemplate.favorite_hold_tier3_bypass_kronos = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier3_bypass_momentum")) configTemplate.favorite_hold_tier3_bypass_momentum = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier3_bypass_pre_order")) configTemplate.favorite_hold_tier3_bypass_pre_order = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier3_bypass_time_ranges")) configTemplate.favorite_hold_tier3_bypass_time_ranges = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "favorite_hold_tier3_bypass_min_odds")) configTemplate.favorite_hold_tier3_bypass_min_odds = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "hedge_enabled")) configTemplate.hedge_enabled = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "hedge_pct")) configTemplate.hedge_pct = 0;
  // Time in min<->max odds range gate (band_dwell): disabled / no-op defaults.
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "pre_decision_avg_odds_enabled")) configTemplate.pre_decision_avg_odds_enabled = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "pre_decision_avg_odds_min")) configTemplate.pre_decision_avg_odds_min = 0.53;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "band_dwell_enabled")) configTemplate.band_dwell_enabled = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "band_dwell_min_seconds")) configTemplate.band_dwell_min_seconds = 0;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "band_dwell_require_exceeds_opposite")) configTemplate.band_dwell_require_exceeds_opposite = false;
  // Max Favorite Flips gate: disabled / no-op defaults.
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "flip_gate_enabled")) configTemplate.flip_gate_enabled = false;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "flip_gate_max_flips")) configTemplate.flip_gate_max_flips = 0;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "flip_gate_skip_seconds")) configTemplate.flip_gate_skip_seconds = 0;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "flip_gate_flip_threshold")) configTemplate.flip_gate_flip_threshold = 0.5;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "entry_confirm_seconds")) configTemplate.entry_confirm_seconds = 0;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "candle_filter_enabled")) configTemplate.candle_filter_enabled = true;
  if (!Object.prototype.hasOwnProperty.call(configTemplate, "sim_execution_delay_seconds")) configTemplate.sim_execution_delay_seconds = 0;
  for (const [k, v] of Object.entries(ENTRY_QUALITY_DEFAULTS)) {
    if (!Object.prototype.hasOwnProperty.call(configTemplate, k)) configTemplate[k] = v;
  }

  const singleInputs: Record<string, string> = {};
  const rangeInputs: Record<string, NumericRange> = {};
  for (const [key, templateValue] of Object.entries(configTemplate)) {
    if (HIDDEN_FIELDS.has(key) || !EDITABLE_KEYS.has(key)) continue;
    if (key === "candle_timeframe_seconds") continue; // handled as checkbox group
    const axisValues = axisByKey.get(key);
    // Numeric decimal fields (thresholds like take_profit_threshold /
    // stop_loss_threshold) must ALWAYS render as Min/Max/Step, even when
    // the stored value is blank/non-numeric (e.g. a config saved with
    // take_profit_threshold=""). Without this guard a blank value slips
    // through `!isNumericTemplate` and the field collapses to a single
    // text box, silently dropping the range-sweep controls.
    const isSingle = typeof templateValue === "boolean"
      || SINGLE_TEXT_FIELDS.has(key)
      || (!isNumericTemplate(templateValue) && !DECIMAL_FIELDS.has(key));
    if (isSingle) {
      if (axisValues && axisValues.length > 1 && BRACKET_SET_SWEEP_FIELDS.has(key)) {
        // "[MON,TUE],[WED,THU]" bracket-set sweep syntax.
        singleInputs[key] = axisValues.map((v) => `[${String(v)}]`).join(",");
      } else if (
        typeof templateValue === "boolean"
        && SWEEPABLE_BOOL_KEYS.has(key)
        && axisValues && axisValues.length > 1
      ) {
        // A saved sweep that varied this bypass toggle off & on -> restore
        // the "Sweep" tri-state control rather than collapsing to Off.
        singleInputs[key] = "sweep";
      } else {
        singleInputs[key] = valueToInput(templateValue);
      }
    } else if (axisValues && axisValues.length > 1) {
      rangeInputs[key] = deriveRangeFromAxisValues(axisValues);
    } else {
      const min = valueToInput(templateValue);
      const defaults = defaultRangeFromMin(min);
      rangeInputs[key] = { min, max: defaults.max, step: defaults.step };
    }
  }

  let candleTfSelected: number[];
  const ctfAxis = axisByKey.get("candle_timeframe_seconds");
  if (ctfAxis && ctfAxis.length > 0) {
    const vals = ctfAxis.map((v) => Number(v)).filter((n) => Number.isFinite(n));
    candleTfSelected = vals.length > 0 ? vals : [0];
  } else {
    const v = Number((fixed as Record<string, unknown>).candle_timeframe_seconds ?? 0);
    candleTfSelected = Number.isFinite(v) && v > 0 ? [v] : [0];
  }

  const range = (sweep.range && typeof sweep.range === "object" && !Array.isArray(sweep.range))
    ? sweep.range as Record<string, unknown>
    : {};

  const sortByRaw = String(sweep.sort_by ?? "pnl");
  const sortBy: SortBy = (["pnl", "wins", "losses", "win_rate"] as SortBy[]).includes(sortByRaw as SortBy)
    ? (sortByRaw as SortBy)
    : "pnl";

  const topK = Number(sweep.top_k_global);
  const minWinsV = Number(sweep.min_wins);
  const minWinRateV = Number(sweep.min_win_rate);
  const maxLossesV = sweep.max_losses;
  const fastScreen = (sweep.fast_screen && typeof sweep.fast_screen === "object" && !Array.isArray(sweep.fast_screen))
    ? sweep.fast_screen as Record<string, unknown>
    : {};
  const etaV = Number(fastScreen.eta);
  const minWindowsV = Number(fastScreen.min_windows);
  const maxKeepV = Number(fastScreen.max_keep);

  return {
    configTemplate,
    singleInputs,
    rangeInputs,
    candleTfSelected,
    from: toLocalDateTimeInput(range.from as string | null | undefined),
    to: toLocalDateTimeInput(range.to as string | null | undefined),
    sortBy,
    topResults: Number.isFinite(topK) && topK > 0 ? Math.trunc(topK) : 10,
    minWins: Number.isFinite(minWinsV) && minWinsV > 0 ? Math.trunc(minWinsV) : 0,
    minWinRate: Number.isFinite(minWinRateV) && minWinRateV > 0
      ? Number((Math.min(1, minWinRateV) * 100).toFixed(2))
      : 0,
    maxLosses: maxLossesV == null
      ? ""
      : (Number.isFinite(Number(maxLossesV)) ? Math.max(0, Math.trunc(Number(maxLossesV))) : ""),
    bestPerLossCount: Boolean(sweep.best_per_loss_count),
    fastScreenEnabled: Boolean(fastScreen.enabled),
    fastScreenEta: Number.isFinite(etaV) ? Math.max(2, Math.trunc(etaV)) : 3,
    fastScreenMinWindows: Number.isFinite(minWindowsV) ? Math.max(0, Math.trunc(minWindowsV)) : 0,
    fastScreenMaxKeep: Number.isFinite(maxKeepV) ? Math.max(0, Math.trunc(maxKeepV)) : 0,
    fastScreenEarlyDedup: fastScreen.early_rung_dedup == null ? true : Boolean(fastScreen.early_rung_dedup),
  };
}

function triggerJsonDownload(filename: string, payload: unknown): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

type FastScreenRecommendation = {
  label: string;
  min: number;
  max: number;
  eta: number;
  windows: number;
  note: string;
};

const FAST_SCREEN_RECOMMENDATIONS: FastScreenRecommendation[] = [
  { label: "< 500K", min: 0, max: 500_000, eta: 3, windows: 20, note: "Fast Screen optional; minimal gains" },
  { label: "500K – 1M", min: 500_000, max: 1_000_000, eta: 3, windows: 20, note: "Conservative pruning" },
  { label: "1M – 5M", min: 1_000_000, max: 5_000_000, eta: 4, windows: 20, note: "Balanced speed/accuracy" },
  { label: "5M – 20M", min: 5_000_000, max: 20_000_000, eta: 5, windows: 20, note: "Aggressive; good for wide sweeps" },
  { label: "20M – 50M", min: 20_000_000, max: 50_000_000, eta: 5, windows: 15, note: "More aggressive pruning" },
  { label: "50M – 100M", min: 50_000_000, max: 100_000_000, eta: 6, windows: 15, note: "Heavy pruning, some risk" },
  { label: "100M – 500M", min: 100_000_000, max: 500_000_000, eta: 7, windows: 10, note: "Very aggressive; expect some misses" },
  { label: "> 500M", min: 500_000_000, max: Infinity, eta: 8, windows: 10, note: "Maximum pruning; validate top-K" },
];

function FastScreenRecommendationsTable({
  comboCount,
  onApply,
}: {
  comboCount: number;
  onApply: (eta: number, windows: number) => void;
}) {
  const activeIndex = FAST_SCREEN_RECOMMENDATIONS.findIndex(
    (r) => comboCount >= r.min && comboCount < r.max,
  );
  return (
    <Stack gap={4}>
      <Text size="sm" fw={600}>
        Recommended Fast Screen settings
      </Text>
      <Text size="xs" c="dimmed">
        Suggested eta / initial windows by total combinations. Click a row to
        apply. Row matching the current combo count is highlighted.
      </Text>
      <Table.ScrollContainer minWidth={480}>
        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Combinations</Table.Th>
              <Table.Th>Eta</Table.Th>
              <Table.Th>Initial Windows</Table.Th>
              <Table.Th>Notes</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {FAST_SCREEN_RECOMMENDATIONS.map((row, i) => (
              <Table.Tr
                key={row.label}
                onClick={() => onApply(row.eta, row.windows)}
                style={{
                  cursor: "pointer",
                  backgroundColor: i === activeIndex ? "var(--mantine-color-blue-light)" : undefined,
                }}
              >
                <Table.Td>
                  <Group gap="xs">
                    <Text size="sm">{row.label}</Text>
                    {i === activeIndex && (
                      <Badge size="xs" color="blue" variant="light">
                        current
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>{row.eta}</Table.Td>
                <Table.Td>{row.windows}</Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {row.note}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  );
}

function fmtCoverageDate(iso: string | null): string {
  if (!iso) return "?";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "?";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// Strict cache-coverage checklist for the Kronos gate. Renders in the Kronos
// Forecast section (only when the gate is enabled). Every swept decision time
// must be cached for the selected signature AND that cache must span the sim's
// date range, or the Run button stays disabled.
function KronosCoveragePanel({
  coverage,
  onUseCacheStart,
  onUseCacheEnd,
}: {
  coverage: KronosCoverageResult;
  onUseCacheStart: () => void;
  onUseCacheEnd: () => void;
}) {
  if (!coverage.enabled) return null;

  if (coverage.loading) {
    return (
      <Text size="sm" c="dimmed">Checking Kronos cache coverage…</Text>
    );
  }

  if (coverage.rows.length === 0) {
    return (
      <Alert color="yellow" variant="light" p="xs">
        <Text size="sm">
          Could not determine which decision times the sweep will evaluate. Set a valid
          decision-time Min/Max/Step above.
        </Text>
      </Alert>
    );
  }

  const allOk = !coverage.blocking;

  return (
    <Stack gap={6}>
      <Text size="xs" c="dimmed">
        Cache signature: <Code style={monoStyle}>{coverage.signature}</Code>
      </Text>

      <Group gap={6}>
        {coverage.rows.map((r) => {
          const bad = !r.cached || r.lowerGap || r.upperGap;
          const label = !r.cached
            ? `${r.dt}s · no cache`
            : r.lowerGap && r.upperGap
              ? `${r.dt}s · out of range`
              : r.lowerGap
                ? `${r.dt}s · starts before`
                : r.upperGap
                  ? `${r.dt}s · ends after`
                  : `${r.dt}s · ${r.count} win`;
          return (
            <Badge
              key={r.dt}
              color={bad ? "red" : "green"}
              variant={bad ? "filled" : "light"}
              title={
                r.cached
                  ? `Cached ${fmtCoverageDate(r.windowMin)}–${fmtCoverageDate(r.windowMax)} (${r.count} windows)`
                  : "No precomputed cache for this signature at this decision time"
              }
            >
              {label}
            </Badge>
          );
        })}
      </Group>

      {allOk ? (
        <Alert color="green" variant="light" p="xs">
          <Text size="sm">
            All {coverage.rows.length} decision time{coverage.rows.length === 1 ? "" : "s"} are
            cached for this signature and cover the selected date range.
          </Text>
        </Alert>
      ) : (
        <Alert color="red" variant="light" p="xs" title="Kronos cache coverage incomplete — Run is blocked">
          <Stack gap={6}>
            {coverage.missing.length > 0 ? (
              <Text size="sm">
                <b>Missing caches</b> for decision time{coverage.missing.length === 1 ? "" : "s"}:{" "}
                <Code style={monoStyle}>{coverage.missing.join(", ")}</Code>. Precompute this
                signature at these times in <b>Settings → Kronos</b>.
              </Text>
            ) : null}

            {coverage.lowerGapDts.length > 0 ? (
              <Stack gap={4}>
                <Text size="sm">
                  <b>Sim starts before the cache.</b> Your <b>From</b> predates the earliest
                  precomputed window{coverage.requiredFrom ? (
                    <> (cache begins <b>{fmtCoverageDate(coverage.requiredFrom)}</b>)</>
                  ) : null} for decision time{coverage.lowerGapDts.length === 1 ? "" : "s"}{" "}
                  <Code style={monoStyle}>{coverage.lowerGapDts.join(", ")}</Code> — those earlier
                  windows would cold-start. Move <b>From</b> up to the cache start, or precompute
                  the earlier windows.
                </Text>
                {coverage.requiredFrom ? (
                  <Group>
                    <Button size="xs" variant="light" onClick={onUseCacheStart}>
                      Set “From” to cache start ({fmtCoverageDate(coverage.requiredFrom)})
                    </Button>
                  </Group>
                ) : null}
              </Stack>
            ) : null}

            {coverage.upperGapDts.length > 0 ? (
              <Stack gap={4}>
                <Text size="sm">
                  <b>Sim ends after the cache.</b>{" "}
                  {coverage.needsExplicitTo ? (
                    <>Your <b>To</b> is unbounded (runs to now), so it extends past the last
                    precomputed window{coverage.requiredTo ? (
                      <> (cache ends <b>{fmtCoverageDate(coverage.requiredTo)}</b>)</>
                    ) : null}.</>
                  ) : (
                    <>Your <b>To</b> postdates the last precomputed window{coverage.requiredTo ? (
                      <> (cache ends <b>{fmtCoverageDate(coverage.requiredTo)}</b>)</>
                    ) : null} for decision time{coverage.upperGapDts.length === 1 ? "" : "s"}{" "}
                    <Code style={monoStyle}>{coverage.upperGapDts.join(", ")}</Code>.</>
                  )}{" "}
                  Move <b>To</b> down to the cache end, or precompute the newer windows.
                </Text>
                {coverage.requiredTo ? (
                  <Group>
                    <Button size="xs" variant="light" onClick={onUseCacheEnd}>
                      Set “To” to cache end ({fmtCoverageDate(coverage.requiredTo)})
                    </Button>
                  </Group>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

export function AutomatedSimulatorPage() {
  const marketCtx = useMarket();
  const marketId = marketCtx.marketId;
  // V3 multi-market: candle_timeframe_seconds sweep is rendered as a
  // checkbox-per-allowed-timeframe (mirroring Config.tsx) instead of
  // Min/Max/Step. We need the active market's window_seconds to filter
  // the allow-list. Falls back to 300s (btc-5m) while the catalogue
  // is still loading.
  const marketWindowSeconds = marketCtx.market?.window_seconds ?? 300;
  const marketBucketSeconds = marketCtx.market?.bucket_seconds ?? 60;
  const candleTimeframeOptions = useMemo(
    () => candleTimeframeOptionsFor(marketWindowSeconds),
    [marketWindowSeconds],
  );
  // V3 multi-market: pull the active config for the currently selected
  // market (rather than the global/legacy active pointer) so that
  // switching the market dropdown can swap the form to that market's
  // active config when one exists. Mirrors the Manual Simulator.
  const activeCfgQ = useQuery({
    queryKey: ["markets", marketId, "config-active", "for-auto-sim"],
    queryFn: () => api.markets.activeConfig(marketId),
    staleTime: 30_000,
    enabled: Boolean(marketId),
  });

  const versionsQ = useQuery({
    queryKey: ["config", "versions", "for-auto-sim"],
    queryFn: async () => {
      const page = await api.config.versions({ limit: 200 });
      return page.items;
    },
    staleTime: 30_000,
  });

  const [selectedConfigVersionId, setSelectedConfigVersionId] = useState<string | null>(null);
  // Load-config deep-link: a saved sweep form imported from the Saved
  // Simulations page (?import_sweep=...). Parsed once on mount. While an
  // import is active the config-version seed + market-active swap effects
  // stand down so they don't clobber the imported form. Cleared when the
  // operator picks a config version from the dropdown.
  const importedSweepRef = useRef<ImportedSweep | null>(parseImportedSweepFromUrl());
  const [importActive, setImportActive] = useState<boolean>(() => importedSweepRef.current != null);
  // Track which market we've already applied an active-config swap for
  // so the effect below fires once per market change (and on initial
  // load) without clobbering the operator's edits across re-renders.
  const [appliedMarketId, setAppliedMarketId] = useState<string | null>(null);
  const [configTemplate, setConfigTemplate] = useState<Record<string, unknown> | null>(null);

  // Single-value fields (booleans, non-numeric strings, special arrays).
  const [singleInputs, setSingleInputs] = useState<Record<string, string>>({});
  // Min/Max/Step fields keyed by config key.
  const [rangeInputs, setRangeInputs] = useState<Record<string, NumericRange>>({});
  // V3 multi-market: ``candle_timeframe_seconds`` is rendered as a
  // checkbox group instead of Min/Max/Step. Each entry stores the
  // seconds value of an allowed timeframe; ``0`` means "use the
  // market's bucket_seconds" (legacy default). Initialised below from
  // the loaded template so the default snapshot replays identically.
  const [candleTfSelected, setCandleTfSelected] = useState<number[]>([0]);
  // When the operator switches markets the allow-list narrows; prune
  // any selected seconds that no longer divide the new window so the
  // sweep never emits engine-rejecting axis values. ``0`` (Default)
  // always survives.
  useEffect(() => {
    const allowed = new Set<number>([0, ...candleTimeframeOptions.map((o) => o.seconds)]);
    setCandleTfSelected((prev) => {
      const filtered = prev.filter((v) => allowed.has(v));
      if (filtered.length === prev.length) return prev;
      return filtered.length > 0 ? filtered : [0];
    });
  }, [candleTimeframeOptions]);
  // Feature column layout: collapsed cards persist per page; "added" cards
  // are features the operator pulled in (or switched off) that should stay
  // visible even while their sweep inputs say inactive.
  const { collapsed: collapsedPanels, toggle: toggleCollapsed, setAll: setAllCollapsed, expand: expandPanel, collapse: collapsePanel } =
    useCollapsedPanels("automated-sim:collapsed-panels");
  const [addedPanels, setAddedPanels] = useState<string[]>([]);
  // Last non-zero input (single value or sweep range) of each value-driven
  // feature, restored when its toggle is switched back on.
  const [stashedValues, setStashedValues] = useState<Record<string, string | NumericRange>>({});

  const [from, setFrom] = useState(DEFAULT_SIM_FROM);
  const [to, setTo] = useState("");

  // Results settings (bottom card)
  const [sortBy, setSortBy] = useState<SortBy>("pnl");
  const [topResults, setTopResults] = useState<number>(10);
  const [minWins, setMinWins] = useState<number>(0);
  const [minWinRate, setMinWinRate] = useState<number>(0);
  // Maximum total losses (losses + stop-losses) a combination may have and
  // still appear in the results. Empty = disabled (no cap).
  const [maxLosses, setMaxLosses] = useState<number | "">("");
  // When enabled, collapse results to the single best combo per distinct
  // total-losses count (highest win amount within each loss bucket).
  const [bestPerLossCount, setBestPerLossCount] = useState(false);
  const [subtractNoFillsFromResults, setSubtractNoFillsFromResults] = useState(false);
  const [excludeStaleOdds, setExcludeStaleOdds] = useState(false);
  // 0009_simulation_batches: local-only scheduling + combo-range batching.
  // batchSize 0 = run the whole sweep at once; > 0 splits into sequential
  // slices of that many combos each (start/stop N sub-runs). scheduleNow
  // starts immediately; otherwise defer to `scheduledStart`.
  const [batchSize, setBatchSize] = useState<number>(0);
  const [scheduleNow, setScheduleNow] = useState(true);
  const [scheduledStart, setScheduledStart] = useState("");
  // Local-only resource governor: cap sweep worker parallelism to a % of
  // CPU cores / physical RAM. Empty = use all cores (default). Soft caps —
  // they reduce the number of worker processes, never hard-kill a run.
  const [cpuPercent, setCpuPercent] = useState<number | "">("");
  const [ramPercent, setRamPercent] = useState<number | "">("");
  // Fast-screen (successive-halving) mode. Off by default; when enabled
  // the sweep runs as a ladder of rungs that prunes weak combos on a
  // small window subset before scoring survivors on all windows.
  const [fastScreenEnabled, setFastScreenEnabled] = useState(false);
  const [fastScreenEta, setFastScreenEta] = useState<number>(3);
  const [fastScreenMinWindows, setFastScreenMinWindows] = useState<number>(0);
  // Absolute cap on how many survivors any single non-final rung may
  // promote (0 = unlimited). Bounds the in-memory survivor pool on huge
  // combo spaces — the parent holds every survivor in RAM, so an
  // uncapped run over tens of millions of combos can exhaust memory.
  const [fastScreenMaxKeep, setFastScreenMaxKeep] = useState<number>(0);
  // Early-rung identical-outcome dedup. On (default) = fast: combos that
  // share an outcome signature on a small early-rung subset merge into one
  // survivor, collapsing the promoted pool by orders of magnitude. Off =
  // thorough but much slower (promotes the full survivor pool each rung).
  const [fastScreenEarlyDedup, setFastScreenEarlyDedup] = useState(true);
  const [simTarget, setSimTarget] = useState<SimTarget>("auto");
  const [modalBatches, setModalBatches] = useState<number>(25);
  const [modalArtifactsDir, setModalArtifactsDir] = useState("modal_artifacts");
  const [modalResumeFromRunId, setModalResumeFromRunId] = useState("");

  const [runId, setRunId] = useState<string | null>(null);
  const [settingsModalRow, setSettingsModalRow] = useState<FinalistRow | null>(null);

  const versionOptions = useMemo(() => {
    const activeId = activeCfgQ.data?.active?.config_version_id;
    return (versionsQ.data ?? []).map((v) => {
      const name = typeof v.config?.config_name === "string" && v.config.config_name.trim()
        ? v.config.config_name.trim()
        : v.config_version_id;
      const activeSuffix = v.config_version_id === activeId ? " · active" : "";
      return { value: v.config_version_id, label: `${name}${activeSuffix}` };
    });
  }, [versionsQ.data, activeCfgQ.data?.active?.config_version_id]);

  const selectedVersion = useMemo(
    () => (versionsQ.data ?? []).find((v) => v.config_version_id === selectedConfigVersionId) ?? null,
    [versionsQ.data, selectedConfigVersionId],
  );

  // V3 multi-market: when the operator switches markets via the
  // global dropdown, swap the form to that market's active config if
  // one exists. When the new market has no active config, leave the
  // current selection untouched so the operator's edits survive the
  // switch. The ``appliedMarketId`` gate ensures this effect runs once
  // per market change rather than re-applying on every re-render.
  useEffect(() => {
    // A loaded sweep config owns the form; don't swap it out from under
    // the operator on market resolution.
    if (importActive) return;
    // Wait until the per-market query has loaded data for the CURRENT
    // marketId (TanStack may still be returning the previous market's
    // data while the new request is in flight).
    if (!activeCfgQ.data || activeCfgQ.data.market_id !== marketId) return;
    if (appliedMarketId === marketId) return;

    const activeId = activeCfgQ.data.active?.config_version_id ?? null;
    if (activeId) {
      setSelectedConfigVersionId(activeId);
    } else if (!selectedConfigVersionId) {
      // First load AND this market has no active config: fall back to
      // the most recent version so the form is at least populated.
      const first = versionsQ.data?.[0]?.config_version_id;
      if (first) setSelectedConfigVersionId(first);
    }
    // else: no active config for the new market, but the operator
    // already has a selection -- keep it so their current form values
    // survive the market switch.
    setAppliedMarketId(marketId);
  }, [marketId, activeCfgQ.data, appliedMarketId, selectedConfigVersionId, versionsQ.data, importActive]);

  // Seed inputs from selected config snapshot.
  useEffect(() => {
    // A loaded sweep config takes precedence over the version-snapshot
    // seed until the operator explicitly picks a config version.
    if (importActive) return;
    if (!selectedVersion) return;
    const cfgRaw = (selectedVersion.config ?? {}) as Record<string, unknown>;
    const cfgWithMomentum = Object.prototype.hasOwnProperty.call(cfgRaw, "odds_momentum_use_unique_odds")
      ? cfgRaw
      : { ...cfgRaw, odds_momentum_use_unique_odds: true };
    const cfg: Record<string, unknown> = { ...cfgWithMomentum };
    if (!Object.prototype.hasOwnProperty.call(cfg, "odds_momentum_avg_lookback")) cfg.odds_momentum_avg_lookback = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "odds_momentum_avg_max_diff")) cfg.odds_momentum_avg_max_diff = 0.5;
    if (!Object.prototype.hasOwnProperty.call(cfg, "compounding_enabled")) cfg.compounding_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "compounding_win_pct")) cfg.compounding_win_pct = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "compounding_max_size_usdc")) cfg.compounding_max_size_usdc = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "compounding_stop_after_wins")) cfg.compounding_stop_after_wins = cfg.compounding_reset_after_wins ?? 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "compounding_stop_size_stay")) cfg.compounding_stop_size_stay = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "compounding_resume_from_saved")) cfg.compounding_resume_from_saved = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "compounding_loss_pct")) cfg.compounding_loss_pct = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "cascading_sizes_enabled")) cfg.cascading_sizes_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "cascading_sizes")) cfg.cascading_sizes = "10.0,7.0,5.0";
    if (!Object.prototype.hasOwnProperty.call(cfg, "trade_days_enabled")) cfg.trade_days_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "trade_days_allowed")) cfg.trade_days_allowed = "MON,TUE,WED,THU,FRI,SAT,SUN";
    if (!Object.prototype.hasOwnProperty.call(cfg, "allow_live_candle")) cfg.allow_live_candle = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "live_candle_minute")) cfg.live_candle_minute = 0;
    // V3 multi-market candle controls. 0 = "use market bucket_seconds"
    // / "no skip" so legacy snapshots replay identical.
    if (!Object.prototype.hasOwnProperty.call(cfg, "candle_timeframe_seconds")) cfg.candle_timeframe_seconds = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "skip_first_candles")) cfg.skip_first_candles = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "open_price_gate_enabled")) cfg.open_price_gate_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "open_price_override_enabled")) cfg.open_price_override_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "open_price_override_within_seconds")) cfg.open_price_override_within_seconds = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "open_price_cushion_enabled")) cfg.open_price_cushion_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "open_price_cushion_min_bps")) cfg.open_price_cushion_min_bps = 2.0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "prev_windows_check_enabled")) cfg.prev_windows_check_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "prev_windows_count")) cfg.prev_windows_count = 3;
    if (!Object.prototype.hasOwnProperty.call(cfg, "prev_windows_min_matches")) cfg.prev_windows_min_matches = 2;
    if (!Object.prototype.hasOwnProperty.call(cfg, "kronos_check_enabled")) cfg.kronos_check_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "kronos_require_direction")) cfg.kronos_require_direction = true;
    if (!Object.prototype.hasOwnProperty.call(cfg, "kronos_min_confidence")) cfg.kronos_min_confidence = 0.5;
    if (!Object.prototype.hasOwnProperty.call(cfg, "kronos_model")) cfg.kronos_model = "small";
    if (!Object.prototype.hasOwnProperty.call(cfg, "kronos_lookback")) cfg.kronos_lookback = 400;
    if (!Object.prototype.hasOwnProperty.call(cfg, "kronos_candle_timeframe_seconds")) cfg.kronos_candle_timeframe_seconds = 60;
    if (!Object.prototype.hasOwnProperty.call(cfg, "kronos_sample_count")) cfg.kronos_sample_count = 5;
    if (!Object.prototype.hasOwnProperty.call(cfg, "kronos_temperature")) cfg.kronos_temperature = 1.0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "kronos_top_p")) cfg.kronos_top_p = 0.9;
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_enabled")) cfg.momentum_sell_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_window_type")) cfg.momentum_sell_window_type = "number";
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_window_value")) cfg.momentum_sell_window_value = 10;
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_sl_enabled")) cfg.momentum_sell_sl_enabled = true;
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_sl_threshold_type")) cfg.momentum_sell_sl_threshold_type = "points";
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_sl_threshold_value")) cfg.momentum_sell_sl_threshold_value = 0.05;
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_sl_require_seconds")) cfg.momentum_sell_sl_require_seconds = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_sl_start_at_seconds_left")) cfg.momentum_sell_sl_start_at_seconds_left = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_tp_enabled")) cfg.momentum_sell_tp_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_tp_threshold_type")) cfg.momentum_sell_tp_threshold_type = "points";
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_tp_threshold_value")) cfg.momentum_sell_tp_threshold_value = 0.05;
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_tp_min_seconds_below")) cfg.momentum_sell_tp_min_seconds_below = 3;
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_tp_start_at_seconds_left")) cfg.momentum_sell_tp_start_at_seconds_left = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_profit_is_win")) cfg.momentum_sell_profit_is_win = true;
    if (!Object.prototype.hasOwnProperty.call(cfg, "momentum_sell_loss_breaks_streak")) cfg.momentum_sell_loss_breaks_streak = true;
    if (!Object.prototype.hasOwnProperty.call(cfg, "loss_streak_continue_threshold_usdc")) cfg.loss_streak_continue_threshold_usdc = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "no_sell_window_seconds")) cfg.no_sell_window_seconds = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "allow_multiple_orders_per_window")) cfg.allow_multiple_orders_per_window = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "candle_filter_enabled")) cfg.candle_filter_enabled = true;
    if (!Object.prototype.hasOwnProperty.call(cfg, "entry_confirm_seconds")) cfg.entry_confirm_seconds = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "sim_execution_delay_seconds")) cfg.sim_execution_delay_seconds = 0;
    for (const [k, v] of Object.entries(ENTRY_QUALITY_DEFAULTS)) {
      if (!Object.prototype.hasOwnProperty.call(cfg, k)) cfg[k] = v;
    }
    // Pre-order metrics (V3). All 19 fields default to the disabled, wide-band
    // shape so a saved snapshot pre-feature runs byte-identical.
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_metrics_enabled")) cfg.pre_order_metrics_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_up_avg_odds_filter_enabled")) cfg.pre_order_up_avg_odds_filter_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_up_avg_odds_min")) cfg.pre_order_up_avg_odds_min = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_up_avg_odds_max")) cfg.pre_order_up_avg_odds_max = 1;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_down_avg_odds_filter_enabled")) cfg.pre_order_down_avg_odds_filter_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_down_avg_odds_min")) cfg.pre_order_down_avg_odds_min = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_down_avg_odds_max")) cfg.pre_order_down_avg_odds_max = 1;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_up_min_odds_filter_enabled")) cfg.pre_order_up_min_odds_filter_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_up_min_odds_min")) cfg.pre_order_up_min_odds_min = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_up_min_odds_max")) cfg.pre_order_up_min_odds_max = 1;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_up_min_pct_filter_enabled")) cfg.pre_order_up_min_pct_filter_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_up_min_pct_min")) cfg.pre_order_up_min_pct_min = -100;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_up_min_pct_max")) cfg.pre_order_up_min_pct_max = 100;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_down_min_odds_filter_enabled")) cfg.pre_order_down_min_odds_filter_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_down_min_odds_min")) cfg.pre_order_down_min_odds_min = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_down_min_odds_max")) cfg.pre_order_down_min_odds_max = 1;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_down_max_pct_filter_enabled")) cfg.pre_order_down_max_pct_filter_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_down_max_pct_min")) cfg.pre_order_down_max_pct_min = -100;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_order_down_max_pct_max")) cfg.pre_order_down_max_pct_max = 100;
    // Counterpart-market override (added after older snapshots). Default to
    // the disabled / no-op shape so a pre-feature snapshot replays identically.
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_override_enabled")) cfg.counterpart_override_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_odds_threshold")) cfg.counterpart_odds_threshold = 0.95;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_decide_within_pct")) cfg.counterpart_decide_within_pct = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_stable_seconds")) cfg.counterpart_stable_seconds = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_require_trend")) cfg.counterpart_require_trend = true;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_follow_direction")) cfg.counterpart_follow_direction = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_follow_exclusive")) cfg.counterpart_follow_exclusive = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_bypass_candle")) cfg.counterpart_bypass_candle = true;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_bypass_open_price")) cfg.counterpart_bypass_open_price = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_bypass_prev_windows")) cfg.counterpart_bypass_prev_windows = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_bypass_kronos")) cfg.counterpart_bypass_kronos = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_bypass_momentum")) cfg.counterpart_bypass_momentum = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_bypass_pre_order")) cfg.counterpart_bypass_pre_order = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_bypass_time_ranges")) cfg.counterpart_bypass_time_ranges = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_bypass_min_odds")) cfg.counterpart_bypass_min_odds = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_bypass_max_odds")) cfg.counterpart_bypass_max_odds = false;
    // Counterpart matching-order (sub-feature): disabled / no-op defaults.
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_match_order_enabled")) cfg.counterpart_match_order_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_match_order_require_trend")) cfg.counterpart_match_order_require_trend = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_match_order_require_candle")) cfg.counterpart_match_order_require_candle = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_match_order_require_open_price")) cfg.counterpart_match_order_require_open_price = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_match_order_require_prev_windows")) cfg.counterpart_match_order_require_prev_windows = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_match_order_require_kronos")) cfg.counterpart_match_order_require_kronos = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_match_order_require_momentum")) cfg.counterpart_match_order_require_momentum = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_match_order_require_pre_order")) cfg.counterpart_match_order_require_pre_order = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_match_order_require_time_ranges")) cfg.counterpart_match_order_require_time_ranges = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_match_order_require_min_odds")) cfg.counterpart_match_order_require_min_odds = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "counterpart_match_order_require_max_odds")) cfg.counterpart_match_order_require_max_odds = false;

    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_enabled")) cfg.favorite_hold_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_flip_threshold")) cfg.favorite_hold_flip_threshold = 0.51;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_skip_seconds")) cfg.favorite_hold_skip_seconds = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier1_enabled")) cfg.favorite_hold_tier1_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier1_min_odds")) cfg.favorite_hold_tier1_min_odds = 0.8;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier1_dwell_seconds")) cfg.favorite_hold_tier1_dwell_seconds = 60;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier2_enabled")) cfg.favorite_hold_tier2_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier2_min_rise")) cfg.favorite_hold_tier2_min_rise = 0.1;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier2_check_seconds")) cfg.favorite_hold_tier2_check_seconds = 120;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier2_require_candle")) cfg.favorite_hold_tier2_require_candle = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier2_require_open_price")) cfg.favorite_hold_tier2_require_open_price = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier2_require_prev_windows")) cfg.favorite_hold_tier2_require_prev_windows = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier2_require_kronos")) cfg.favorite_hold_tier2_require_kronos = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier2_require_momentum")) cfg.favorite_hold_tier2_require_momentum = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier2_require_pre_order")) cfg.favorite_hold_tier2_require_pre_order = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier2_require_time_ranges")) cfg.favorite_hold_tier2_require_time_ranges = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier2_require_min_odds")) cfg.favorite_hold_tier2_require_min_odds = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier3_enabled")) cfg.favorite_hold_tier3_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier3_bypass_candle")) cfg.favorite_hold_tier3_bypass_candle = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier3_bypass_open_price")) cfg.favorite_hold_tier3_bypass_open_price = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier3_bypass_prev_windows")) cfg.favorite_hold_tier3_bypass_prev_windows = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier3_bypass_kronos")) cfg.favorite_hold_tier3_bypass_kronos = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier3_bypass_momentum")) cfg.favorite_hold_tier3_bypass_momentum = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier3_bypass_pre_order")) cfg.favorite_hold_tier3_bypass_pre_order = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier3_bypass_time_ranges")) cfg.favorite_hold_tier3_bypass_time_ranges = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "favorite_hold_tier3_bypass_min_odds")) cfg.favorite_hold_tier3_bypass_min_odds = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "hedge_enabled")) cfg.hedge_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "hedge_pct")) cfg.hedge_pct = 0;
    // Time in min<->max odds range gate (band_dwell): disabled / no-op defaults.
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_decision_avg_odds_enabled")) cfg.pre_decision_avg_odds_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "pre_decision_avg_odds_min")) cfg.pre_decision_avg_odds_min = 0.53;
    if (!Object.prototype.hasOwnProperty.call(cfg, "band_dwell_enabled")) cfg.band_dwell_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "band_dwell_min_seconds")) cfg.band_dwell_min_seconds = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "band_dwell_require_exceeds_opposite")) cfg.band_dwell_require_exceeds_opposite = false;
    // Max Favorite Flips gate: disabled / no-op defaults.
    if (!Object.prototype.hasOwnProperty.call(cfg, "flip_gate_enabled")) cfg.flip_gate_enabled = false;
    if (!Object.prototype.hasOwnProperty.call(cfg, "flip_gate_max_flips")) cfg.flip_gate_max_flips = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "flip_gate_skip_seconds")) cfg.flip_gate_skip_seconds = 0;
    if (!Object.prototype.hasOwnProperty.call(cfg, "flip_gate_flip_threshold")) cfg.flip_gate_flip_threshold = 0.5;
    setConfigTemplate(cfg);

    const nextSingle: Record<string, string> = {};
    const nextRange: Record<string, NumericRange> = {};
    for (const [key, value] of Object.entries(cfg)) {
      if (HIDDEN_FIELDS.has(key) || !EDITABLE_KEYS.has(key)) continue;
      // V3 multi-market: candle_timeframe_seconds is rendered as a
      // checkbox group below, NOT a Min/Max/Step range. Skip the
      // numeric-range bucketing so it doesn't appear twice.
      if (key === "candle_timeframe_seconds") continue;
      // See reconstructSweepFormState: decimal numeric fields always get
      // Min/Max/Step so a blank stored value (e.g. take_profit_threshold="")
      // doesn't collapse them to a single text input.
      if (typeof value === "boolean" || SINGLE_TEXT_FIELDS.has(key) || (!isNumericTemplate(value) && !DECIMAL_FIELDS.has(key))) {
        nextSingle[key] = valueToInput(value);
      } else {
        const min = valueToInput(value);
        const defaults = defaultRangeFromMin(min);
        nextRange[key] = { min, max: defaults.max, step: defaults.step };
      }
    }
    // Seed candle-timeframe checkboxes from the loaded snapshot:
    // a single configured value -> exactly that box checked; 0 or
    // missing -> Default (bucket_seconds) checked. This makes the
    // baseline sweep replay byte-identical to the snapshot.
    const tplTf = Number(cfg.candle_timeframe_seconds ?? 0);
    setCandleTfSelected(Number.isFinite(tplTf) && tplTf > 0 ? [tplTf] : [0]);
    setSingleInputs(nextSingle);
    setRangeInputs(nextRange);
    setAddedPanels([]);
  }, [selectedVersion?.config_version_id]);

  // Apply a loaded sweep config (deep-linked from Saved Simulations) once
  // on mount, reconstructing every form control from the saved sweep form.
  // Runs before/independently of the version-snapshot seed (which stands
  // down while ``importActive``), then strips the import params from the
  // URL so a refresh doesn't re-import.
  useEffect(() => {
    const imported = importedSweepRef.current;
    if (!imported) return;
    const recon = reconstructSweepFormState(imported.sweep);
    setConfigTemplate(recon.configTemplate);
    setSingleInputs(recon.singleInputs);
    setRangeInputs(recon.rangeInputs);
    setCandleTfSelected(recon.candleTfSelected);
    setFrom(recon.from);
    setTo(recon.to);
    setSortBy(recon.sortBy);
    setTopResults(recon.topResults);
    setMinWins(recon.minWins);
    setMinWinRate(recon.minWinRate);
    setMaxLosses(recon.maxLosses);
    setBestPerLossCount(recon.bestPerLossCount);
    setSubtractNoFillsFromResults(imported.subtractNoFills);
    setExcludeStaleOdds(imported.excludeStaleOdds);
    setFastScreenEnabled(recon.fastScreenEnabled);
    setFastScreenEta(recon.fastScreenEta);
    setFastScreenMinWindows(recon.fastScreenMinWindows);
    setFastScreenMaxKeep(recon.fastScreenMaxKeep);
    setFastScreenEarlyDedup(recon.fastScreenEarlyDedup);
    setAddedPanels([]);

    const currentHash = window.location.hash || "";
    const cleanHash = currentHash.includes("?")
      ? currentHash.slice(0, currentHash.indexOf("?"))
      : currentHash;
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${cleanHash}`);
    notifications.show({
      title: "Config loaded",
      message: "Loaded the saved sweep config into the Automated Simulator. Review and run.",
      color: "green",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build the sweep payload from the form state.
  const sweepPayload = useMemo(() => {
    if (!configTemplate) return null;

    const fixed: Record<string, unknown> = {};
    const axes: Array<{ key: string; values: unknown[] }> = [];

    // A feature group whose enable toggle is OFF has its sub-field values
    // ignored by the engine, so sweeping Min/Max/Step across them produces
    // millions of *identical* combos (only collapsed by the final Fast
    // Screen dedup, after they've inflated the count + wasted compute).
    // Collapse those disabled sub-fields to a single fixed value so a
    // turned-off group contributes nothing to the combo space.
    const disabledFeatureKeys = new Set<string>();
    for (const group of FEATURE_GROUPS) {
      if (group.enabledKey && singleInputs[group.enabledKey] !== "true") {
        for (const k of group.keys) disabledFeatureKeys.add(k);
      }
    }

    // V3 multi-market: ``live_candle_minute`` is a 1-based candle index, so
    // its valid ceiling is the number of candles in the window -- which
    // depends on the candle timeframe being swept. The engine silently
    // treats an out-of-range index as "never fires" (not an error), so
    // without a clamp the sweep can enumerate e.g. index 7 on a 10-min /
    // 1 h window that only has 6 candles and surface it as a "winner". When
    // multiple timeframes are swept we clamp to the COARSEST one (fewest
    // candles) so every emitted index stays valid for every combo.
    const selectedTfSeconds = (candleTfSelected.length > 0 ? candleTfSelected : [0])
      .map((s) => (s > 0 ? s : Math.max(60, marketBucketSeconds)));
    const maxLiveCandleIndex = Math.max(
      1,
      Math.min(...selectedTfSeconds.map((tf) => Math.floor(marketWindowSeconds / Math.max(1, tf)))),
    );

    // Start from the template so HIDDEN / non-editable fields are
    // preserved verbatim — the sim-bot requires every REQUIRED field.
    for (const [key, templateValue] of Object.entries(configTemplate)) {
      if (HIDDEN_FIELDS.has(key) || !EDITABLE_KEYS.has(key)) {
        fixed[key] = templateValue;
        continue;
      }

      // V3 multi-market: candle_timeframe_seconds is emitted from
      // ``candleTfSelected`` (checkbox state). Behavior:
      //   - 0 boxes checked -> defensive fallback to fixed 0 (use
      //     market.bucket_seconds, same as the legacy default).
      //   - 1 box checked   -> fixed value (no axis, no combo blow-up).
      //   - 2+ checked      -> axis sweep across the chosen values.
      if (key === "candle_timeframe_seconds") {
        const vals = candleTfSelected.filter((n) => Number.isFinite(n) && n >= 0);
        if (vals.length === 0) {
          fixed[key] = 0;
        } else if (vals.length === 1) {
          fixed[key] = vals[0];
        } else {
          // Sort so the combo order is deterministic across renders.
          axes.push({ key, values: [...vals].sort((a, b) => a - b) });
        }
        continue;
      }

      if (key in singleInputs) {
        const raw = singleInputs[key]!;
        if (typeof templateValue === "boolean") {
          // "sweep" -> enumerate both off & on (an axis) so the run covers
          // every combination of the selected bypass toggles. Collapsed to a
          // single fixed value when the owning feature group is off.
          if (SWEEPABLE_BOOL_KEYS.has(key) && raw === "sweep") {
            if (disabledFeatureKeys.has(key)) {
              fixed[key] = false;
            } else {
              axes.push({ key, values: [false, true] });
            }
          } else {
            fixed[key] = raw === "true";
          }
        } else if (typeof templateValue === "number") {
          const n = Number(raw);
          fixed[key] = Number.isFinite(n) ? n : templateValue;
        } else if (BRACKET_SET_SWEEP_FIELDS.has(key)) {
          const parsed = parseBracketSetSweep(raw);
          if (parsed == null) {
            // Malformed bracket syntax — send the raw string through as
            // fixed so the operator sees a downstream validation error
            // rather than silently dropping the field.
            fixed[key] = raw;
          } else if (parsed.kind === "axis") {
            if (disabledFeatureKeys.has(key)) {
              fixed[key] = parsed.values[0];
            } else {
              axes.push({ key, values: parsed.values });
            }
          } else {
            fixed[key] = parsed.value;
          }
        } else {
          fixed[key] = raw;
        }
        continue;
      }

      if (key in rangeInputs) {
        const result = enumerateAxisValues(rangeInputs[key]!, templateValue, DECIMAL_FIELDS.has(key));
        if (result.kind === "axis") {
          let values = result.values;
          // Clamp the live-candle index axis to the window's candle count so
          // the sweep never enumerates (and never "wins" with) an index that
          // exceeds the number of candles for the selected timeframe. Keep 0
          // ("any candle") and drop everything above the ceiling; dedupe in
          // case the clamp collapses several values onto the ceiling.
          if (key === "live_candle_minute") {
            const clamped = values.filter((v) => Number(v) <= maxLiveCandleIndex);
            values = clamped.length > 0 ? clamped : [0];
          }
          if (disabledFeatureKeys.has(key)) {
            fixed[key] = values[0];
          } else if (values.length <= 1) {
            fixed[key] = values[0];
          } else {
            axes.push({ key, values });
          }
        } else {
          // Even a fixed live-candle index can be out of range (e.g. a
          // loaded config generated on a larger window); clamp it too.
          if (key === "live_candle_minute" && Number(result.value) > maxLiveCandleIndex) {
            fixed[key] = maxLiveCandleIndex;
          } else {
            fixed[key] = result.value;
          }
        }
        continue;
      }

      fixed[key] = templateValue;
    }

    const safeTopK = Math.max(1, Math.trunc(topResults || 0));
    // The sim-bot rejects top_k_global > top_n_local; keep local heap
    // comfortably wider than the global ask so ties resolve cleanly.
    const topNLocal = Math.max(safeTopK, 50);

    return {
      fixed,
      axes,
      range: { from: toIsoOrUndef(from) ?? null, to: toIsoOrUndef(to) ?? null },
      top_n_local: topNLocal,
      top_k_global: safeTopK,
      sort_by: sortBy,
      min_wins: Math.max(0, Math.trunc(minWins || 0)),
      min_win_rate: Math.min(1, Math.max(0, (Number(minWinRate) || 0) / 100)),
      max_losses: typeof maxLosses === "number" && Number.isFinite(maxLosses)
        ? Math.max(0, Math.trunc(maxLosses))
        : null,
      best_per_loss_count: bestPerLossCount,
      fast_screen: {
        enabled: fastScreenEnabled,
        eta: Math.max(2, Math.trunc(fastScreenEta || 3)),
        min_windows: Math.max(0, Math.trunc(fastScreenMinWindows || 0)),
        max_keep: Math.max(0, Math.trunc(fastScreenMaxKeep || 0)),
        early_rung_dedup: fastScreenEarlyDedup,
      },
    };
  }, [configTemplate, singleInputs, rangeInputs, candleTfSelected, marketWindowSeconds, marketBucketSeconds, from, to, sortBy, topResults, minWins, minWinRate, maxLosses, bestPerLossCount, fastScreenEnabled, fastScreenEta, fastScreenMinWindows, fastScreenMaxKeep, fastScreenEarlyDedup]);

  const comboCount = useMemo(() => {
    if (!sweepPayload) return 0;
    return sweepPayload.axes.reduce((acc, a) => acc * a.values.length, 1);
  }, [sweepPayload]);

  // ---- Kronos strict cache-coverage validation ------------------------
  // When the Kronos gate is enabled, EVERY decision time the sweep will
  // evaluate must have a precomputed cache for the selected signature, AND
  // that cache must span the sim's date range. Otherwise those windows
  // cold-start and the gate fails closed per window (silent 0 trades). We
  // block the Run button until coverage is complete.
  const kronosEnabled = singleInputs["kronos_check_enabled"] === "true";

  const kronosSignaturesQ = useQuery({
    // Same key as KronosCachePicker so the two share one cached fetch.
    queryKey: ["kronos-signatures", marketId],
    queryFn: () => api.kronos.signatures(marketId as string),
    enabled: Boolean(marketId) && kronosEnabled,
    staleTime: 15_000,
  });

  // Decision times the sweep will actually evaluate — mirrors the built
  // payload exactly (an axis when swept, else the single fixed value).
  const kronosDecisionTimes = useMemo<number[]>(() => {
    if (!sweepPayload) return [];
    const axis = sweepPayload.axes.find((a) => a.key === "decision_time_seconds");
    if (axis) {
      return axis.values
        .map((v) => Math.trunc(Number(v)))
        .filter((n) => Number.isFinite(n));
    }
    const fx = Number((sweepPayload.fixed as Record<string, unknown>)["decision_time_seconds"]);
    return Number.isFinite(fx) ? [Math.trunc(fx)] : [];
  }, [sweepPayload]);

  const kronosSignature = useMemo(
    () => kronosSignatureFromParams({
      model: String(singleInputs["kronos_model"] ?? configTemplate?.kronos_model ?? "small"),
      lookback: Number(configTemplate?.kronos_lookback ?? 400),
      timeframe: Number(configTemplate?.kronos_candle_timeframe_seconds ?? 60),
      sampleCount: Number(configTemplate?.kronos_sample_count ?? 5),
      temperature: Number(configTemplate?.kronos_temperature ?? 1),
      topP: Number(configTemplate?.kronos_top_p ?? 0.9),
    }),
    [singleInputs, configTemplate],
  );

  const kronosCoverage = useMemo(() => {
    if (!kronosEnabled) {
      return {
        enabled: false, blocking: false, loading: false,
        rows: [] as KronosCoverageRow[], missing: [] as number[],
        lowerGapDts: [] as number[], upperGapDts: [] as number[],
        needsExplicitTo: false, signature: kronosSignature,
        requiredFrom: null as string | null, requiredTo: null as string | null,
      };
    }
    const loading = kronosSignaturesQ.isLoading;
    const items = (kronosSignaturesQ.data?.items ?? []).filter((it) => it.signature === kronosSignature);
    const byDt = new Map<number, KronosSignatureInfo>();
    for (const it of items) byDt.set(Math.trunc(it.decision_time_seconds), it);

    // datetime-local strings have no tz (parsed as local); window_start_* are
    // UTC ISO. Date.parse normalises both to epoch ms so comparisons are valid.
    const fromEpoch = Date.parse(from || DEFAULT_SIM_FROM);
    const toTrimmed = to.trim();
    const toSet = toTrimmed !== "";
    const toEpoch = toSet ? Date.parse(toTrimmed) : null;

    const rows: KronosCoverageRow[] = [];
    const missing: number[] = [];
    const lowerGapDts: number[] = [];
    const upperGapDts: number[] = [];
    // requiredFrom = LATEST per-dt window_start_min (the most restrictive
    // start); requiredTo = EARLIEST per-dt window_start_max. Aligning the sim
    // From/To to these guarantees every swept dt's cache brackets the range.
    let requiredFromEpoch = -Infinity;
    let requiredFrom: string | null = null;
    let requiredToEpoch = Infinity;
    let requiredTo: string | null = null;
    for (const dt of kronosDecisionTimes) {
      const hit = byDt.get(dt);
      if (!hit) {
        rows.push({ dt, cached: false, count: 0, windowMin: null, windowMax: null, lowerGap: false, upperGap: false });
        missing.push(dt);
        continue;
      }
      const cMin = hit.window_start_min ? Date.parse(hit.window_start_min) : NaN;
      const cMax = hit.window_start_max ? Date.parse(hit.window_start_max) : NaN;
      if (Number.isFinite(cMin) && cMin > requiredFromEpoch) {
        requiredFromEpoch = cMin;
        requiredFrom = hit.window_start_min;
      }
      if (Number.isFinite(cMax) && cMax < requiredToEpoch) {
        requiredToEpoch = cMax;
        requiredTo = hit.window_start_max;
      }
      const lowerGap = !Number.isFinite(cMin) || (Number.isFinite(fromEpoch) && fromEpoch < cMin);
      const upperGap = !toSet || !Number.isFinite(cMax) || (toEpoch != null && toEpoch > cMax);
      rows.push({
        dt, cached: true, count: hit.count,
        windowMin: hit.window_start_min, windowMax: hit.window_start_max,
        lowerGap, upperGap,
      });
      if (lowerGap) lowerGapDts.push(dt);
      if (upperGap) upperGapDts.push(dt);
    }
    const needsExplicitTo = !toSet && kronosDecisionTimes.length > 0;
    // Strict: block Run until coverage is confirmed complete. While the
    // signatures query is still loading we can't verify, so stay blocked.
    const blocking = loading || missing.length > 0 || lowerGapDts.length > 0 || upperGapDts.length > 0;
    return {
      enabled: true, blocking, loading, rows, missing, lowerGapDts, upperGapDts,
      needsExplicitTo, signature: kronosSignature, requiredFrom, requiredTo,
    };
  }, [kronosEnabled, kronosSignaturesQ.data, kronosSignaturesQ.isLoading, kronosSignature, kronosDecisionTimes, from, to]);

  const coverageBlockSummary = useMemo(() => {
    const parts: string[] = [];
    if (kronosCoverage.missing.length > 0) {
      parts.push(`no cache for decision time(s) ${kronosCoverage.missing.join(", ")}`);
    }
    if (kronosCoverage.lowerGapDts.length > 0) {
      parts.push(`sim range starts before the cache for decision time(s) ${kronosCoverage.lowerGapDts.join(", ")}`);
    }
    if (kronosCoverage.upperGapDts.length > 0) {
      parts.push(`sim range ends after the cache for decision time(s) ${kronosCoverage.upperGapDts.join(", ")}`);
    }
    return parts.length > 0 ? `${parts.join("; ")}.` : "";
  }, [kronosCoverage.missing, kronosCoverage.lowerGapDts, kronosCoverage.upperGapDts]);

  const activeQ = useQuery({
    queryKey: ["simulations", "active", "sweep"],
    queryFn: () => api.simulations.active({ mode: "sweep" }),
    refetchInterval: 1_000,
  });

  const activeRuns = activeQ.data ?? [];
  const hasRunningSweep = activeRuns.some((run) => run.status === "running");

  const savedRunsQ = useQuery({
    queryKey: ["simulations", "saved", "sweep", "automated-page"],
    queryFn: () => api.simulations.saved({ mode: "sweep", target: "modal" }),
    refetchInterval: 15_000,
  });

  const modalSavedRuns = savedRunsQ.data ?? [];

  const modalReadinessQ = useQuery({
    queryKey: ["simulations", "modal-readiness"],
    queryFn: () => api.simulations.modalReadiness(),
    enabled: simTarget === "modal",
    refetchInterval: 20_000,
  });

  useEffect(() => {
    if (runId || activeRuns.length === 0) return;
    const preferred = activeRuns.find((run) => run.status === "running") ?? activeRuns[0]!;
    setRunId(preferred.run_id);
  }, [runId, activeRuns]);

  const runQ = useQuery({
    queryKey: ["simulations", runId],
    queryFn: () => api.simulations.get(runId!),
    enabled: Boolean(runId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && FINAL_STATUSES.has(status)) return false;
      if (status === "queued") return 700;
      return 300;
    },
  });

  const resumeMut = useMutation({
    mutationFn: () => {
      assertSweepTradeTimeRangesMax(sweepPayload);
      return api.simulations.modalResume({
        mode: "sweep",
        target: "modal",
        sweep: sweepPayload,
        modal_artifacts_dir: modalArtifactsDir.trim() || "modal_artifacts",
        modal_resume_only: true,
        modal_resume_from_run_id: modalResumeFromRunId.trim() || undefined,
        subtract_no_fills_from_results: subtractNoFillsFromResults,
        exclude_stale_odds: excludeStaleOdds,
      });
    },
    onSuccess: (run) => {
      setRunId(run.run_id);
      activeQ.refetch();
      savedRunsQ.refetch();
      notifications.show({
        title: "Modal resume started",
        message: `Run ${run.run_id} queued to aggregate existing modal artifacts.`,
        color: "violet",
      });
    },
    onError: (error) => {
      notifications.show({
        title: "Failed to resume from modal artifacts",
        message: extractErrorMessage(error),
        color: "red",
      });
    },
  });

  const cancelMut = useMutation({
    mutationFn: (targetRunId: string) => api.simulations.cancel(targetRunId),
    onSuccess: (run, targetRunId) => {
      if (runId === targetRunId) runQ.refetch();
      activeQ.refetch();
      const alreadyTerminal = FINAL_STATUSES.has(run.status);
      notifications.show({
        title: alreadyTerminal ? "Simulation already finished" : "Stop requested",
        message: alreadyTerminal
          ? `Run ${run.run_id} is ${run.status}.`
          : `Run ${run.run_id} is being cancelled.`,
        color: alreadyTerminal ? "gray" : "orange",
      });
    },
    onError: (error) => {
      notifications.show({
        title: "Failed to stop simulation",
        message: extractErrorMessage(error),
        color: "red",
      });
    },
  });

  const pauseMut = useMutation({
    mutationFn: (targetRunId: string) => api.simulations.pause(targetRunId),
    onSuccess: (_run, targetRunId) => {
      activeQ.refetch();
      if (runId === targetRunId) runQ.refetch();
      notifications.show({
        title: "Batch paused",
        message: `Run ${targetRunId} will stop after the current batch finishes.`,
        color: "yellow",
      });
    },
    onError: (error) => {
      notifications.show({
        title: "Failed to pause",
        message: extractErrorMessage(error),
        color: "red",
      });
    },
  });

  const resumeMut2 = useMutation({
    mutationFn: (targetRunId: string) => api.simulations.resume(targetRunId),
    onSuccess: (_run, targetRunId) => {
      activeQ.refetch();
      if (runId === targetRunId) runQ.refetch();
      notifications.show({
        title: "Batch resumed",
        message: `Run ${targetRunId} will continue with the next batch.`,
        color: "blue",
      });
    },
    onError: (error) => {
      notifications.show({
        title: "Failed to resume",
        message: extractErrorMessage(error),
        color: "red",
      });
    },
  });

  const deleteQueuedMut = useMutation({
    mutationFn: async (targetRunId: string) => {
      await api.simulations.cancel(targetRunId);
      return await api.simulations.delete(targetRunId);
    },
    onSuccess: (_resp, targetRunId) => {
      if (runId === targetRunId) setRunId(null);
      activeQ.refetch();
      runQ.refetch();
      notifications.show({
        title: "Queued simulation removed",
        message: `Run ${targetRunId} was deleted from the queue.`,
        color: "gray",
      });
    },
    onError: (error) => {
      notifications.show({
        title: "Failed to delete queued simulation",
        message: extractErrorMessage(error),
        color: "red",
      });
    },
  });

  const runMut = useMutation({
    mutationFn: () => {
      assertSweepTradeTimeRangesMax(sweepPayload);
      return api.simulations.run({
        mode: "sweep",
        target: simTarget,
        sweep: sweepPayload ?? undefined,
        modal_batches: simTarget === "modal"
          ? Math.max(1, Math.trunc(Number(modalBatches) || 1))
          : undefined,
        modal_artifacts_dir: simTarget === "modal" && modalArtifactsDir.trim()
          ? modalArtifactsDir.trim()
          : undefined,
        subtract_no_fills_from_results: subtractNoFillsFromResults,
        exclude_stale_odds: excludeStaleOdds,
        market_id: marketId,
        // 0009: scheduling + batching apply to local (bullmq) sweeps only.
        batch_size: simTarget !== "modal" && batchSize > 0
          ? Math.max(0, Math.trunc(batchSize))
          : undefined,
        scheduled_start_at: simTarget !== "modal" && !scheduleNow && scheduledStart
          ? (toIsoOrUndef(scheduledStart) ?? undefined)
          : undefined,
        cpu_percent: simTarget !== "modal" && typeof cpuPercent === "number"
          ? cpuPercent
          : undefined,
        ram_percent: simTarget !== "modal" && typeof ramPercent === "number"
          ? Math.min(90, Math.max(1, ramPercent))
          : undefined,
      });
    },
    onMutate: () => ({ hadRunningSweep: hasRunningSweep }),
    onSuccess: (run, _vars, ctx) => {
      setRunId(run.run_id);
      activeQ.refetch();
      const queuedBehindExisting = Boolean(ctx?.hadRunningSweep) || run.status === "queued";
      notifications.show({
        title: queuedBehindExisting ? "Automated simulation queued" : "Automated simulation started",
        message: queuedBehindExisting
          ? `Run ${run.run_id} is queued behind an active simulation.`
          : `Run ${run.run_id} dispatched to ${simTarget === "modal" ? "Modal" : "sim-bot"}.`,
        color: "blue",
      });
    },
    onError: (error) => {
      notifications.show({
        title: "Failed to start automated simulation",
        message: extractErrorMessage(error),
        color: "red",
      });
    },
  });

  const discardMut = useMutation({
    mutationFn: () => api.simulations.delete(runId!),
    onSuccess: () => {
      const droppedId = runId;
      setRunId(null);
      notifications.show({
        title: "Results discarded",
        message: `Run ${droppedId} was deleted.`,
        color: "gray",
      });
    },
    onError: (error) =>
      notifications.show({
        title: "Failed to discard results",
        message: extractErrorMessage(error),
        color: "red",
      }),
  });

  const summary = (runQ.data?.result_summary ?? null) as Record<string, unknown> | null;
  const noFillSubtractionEnabled = Boolean(summary?.subtract_no_fills_from_results);
  const staleOddsExcludedEnabled = Boolean(summary?.exclude_stale_odds);
  const combosEvaluated = summary?.combos_evaluated == null ? null : Number(summary.combos_evaluated);
  const combosTotal = summary?.combo_count_total == null ? null : Number(summary.combo_count_total);
  const runLogs = useMemo<SimulationLogRow[]>(() => {
    if (!summary || !Array.isArray(summary.logs)) return [];
    return (summary.logs as Array<Record<string, unknown>>)
      .map((row) => {
        const level: SimulationLogRow["level"] = row.level === "warn" || row.level === "error"
          ? row.level
          : "info";
        return {
          ts: String(row.ts ?? ""),
          level,
          message: String(row.message ?? ""),
        };
      })
      .filter((row) => row.ts || row.message)
      .slice(-200);
  }, [summary]);
  const runIssue = useMemo(
    () => summarizeSimIssue({ status: runQ.data?.status, error: runQ.data?.error, logs: runLogs }),
    [runQ.data?.status, runQ.data?.error, runLogs],
  );

  const finalists = useMemo<FinalistRow[]>(() => {
    if (!summary || !Array.isArray(summary.finalists)) return [];
    return (summary.finalists as Array<Record<string, unknown>>).map((f) => ({
      combo_index: Number(f.combo_index ?? 0),
      settings: (f.settings as Record<string, unknown>) ?? {},
      combination_variations: Number(f.combination_variations ?? 1),
      wins: Number(f.wins ?? 0),
      losses: Number(f.losses ?? 0),
      take_profits: Number(f.take_profits ?? 0),
      stop_losses: Number(f.stop_losses ?? 0),
      momentum_sells: Number(f.momentum_sells ?? 0),
      momentum_sell_profit_count: Number(f.momentum_sell_profit_count ?? 0),
      momentum_sell_loss_count: Number(f.momentum_sell_loss_count ?? 0),
      momentum_sell_profit_pnl_usdc: Number(f.momentum_sell_profit_pnl_usdc ?? 0),
      momentum_sell_loss_usdc: Number(f.momentum_sell_loss_usdc ?? 0),
      total_losses: Number(
        f.total_losses
          ?? (Number(f.losses ?? 0) + Number(f.stop_losses ?? 0)),
      ),
      take_profit_total_won_usdc: Number(f.take_profit_total_won_usdc ?? 0),
      stop_loss_total_lost_usdc: Number(f.stop_loss_total_lost_usdc ?? 0),
      momentum_sell_total_pnl_usdc: Number(f.momentum_sell_total_pnl_usdc ?? 0),
      no_fill_wins: Number(f.no_fill_wins ?? 0),
      no_fill_losses: Number(f.no_fill_losses ?? 0),
      skips: Number(f.skips ?? 0),
      blocked: Number(f.blocked ?? 0),
      no_bet: Number(f.no_bet ?? 0),
      unresolved: Number(f.unresolved ?? 0),
      trades: Number(f.trades ?? 0),
      win_rate: Number(f.win_rate ?? 0),
      est_pnl_usdc: Number(f.est_pnl_usdc ?? 0),
      fees_usdc: Number(f.fees_usdc ?? 0),
      average_odds: f.average_odds == null ? null : Number(f.average_odds),
    }));
  }, [summary]);

  const topFinalist = finalists[0] ?? null;

  const exportModalConfig = () => {
    if (!sweepPayload) return;
    const filePayload = {
      ...sweepPayload,
      subtract_no_fills_from_results: subtractNoFillsFromResults,
      exclude_stale_odds: excludeStaleOdds,
    };

    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    triggerJsonDownload(`modal-sweep-config-${stamp}.json`, filePayload);
    notifications.show({
      title: "Modal config exported",
      message: "Downloaded raw sweep form JSON compatible with modal runner --form.",
      color: "blue",
    });
  };


  function updateFeatureToggle(featureTitle: string, key: string, checked: boolean) {
    setSingleInputs((prev) => ({ ...prev, [key]: checked ? "true" : "false" }));
    if (checked) {
      expandPanel(featureTitle);
    } else {
      // Keep the card on the page while it is merely switched off (only
      // the explicit Remove action hides it), but fold it up so the
      // disabled settings stop taking space.
      collapsePanel(featureTitle);
      setAddedPanels((prev) => (prev.includes(featureTitle) ? prev : [...prev, featureTitle]));
    }
  }

  const sectionMap = useMemo(
    () => FEATURE_GROUPS
      .map((group) => ({
        title: group.title,
        description: group.description,
        enabledKey: group.enabledKey,
        metaKey: group.metaKey ?? group.enabledKey,
        // V3 multi-market: candle_timeframe_seconds is rendered from
        // its own ``candleTfSelected`` state (checkbox group), so it
        // is NOT in singleInputs/rangeInputs but must still be shown.
        keys: group.keys.filter((k) => k === "candle_timeframe_seconds" || k in singleInputs || k in rangeInputs),
      }))
      .filter((group) => (group.enabledKey && group.enabledKey in singleInputs) || group.keys.length > 0),
    [singleInputs, rangeInputs],
  );

  // A value-driven feature counts as active when its field has (or sweeps
  // to) a value > 0. Range inputs hold min=max=template value when not
  // actually swept, so inspect the bounds rather than mere presence.
  const sweptKeys = useMemo(() => new Set(
    Object.entries(rangeInputs)
      .filter(([, r]) => Number(r.max) > 0 || Number(r.min) > 0)
      .map(([k]) => k),
  ), [rangeInputs]);
  const visibleSections = useMemo(
    () => sectionMap.filter((s) => isSectionActive(s, singleInputs, { extraActiveKeys: sweptKeys }) || addedPanels.includes(s.title)),
    [sectionMap, singleInputs, sweptKeys, addedPanels],
  );
  const availableSections = useMemo(() => {
    const shown = new Set(visibleSections.map((s) => s.title));
    return sectionMap.filter((s) => !shown.has(s.title));
  }, [sectionMap, visibleSections]);
  const orderedSections = useSortedSections(visibleSections);

  function addSection(section: FeatureSectionLike) {
    setAddedPanels((prev) => (prev.includes(section.title) ? prev : [...prev, section.title]));
    if (section.enabledKey) {
      setSingleInputs((prev) => ({ ...prev, [section.enabledKey!]: "true" }));
    }
    expandPanel(section.title);
  }

  function removeSection(section: FeatureSectionLike) {
    setAddedPanels((prev) => prev.filter((t) => t !== section.title));
    const field = valueFieldFor(section);
    if (section.enabledKey) {
      setSingleInputs((prev) => ({ ...prev, [section.enabledKey!]: "false" }));
    } else if (field) {
      setValueFieldRange(field, "0");
    }
  }

  // Value-driven numeric features are sweepable, so the page renders them
  // as a min/max/step range. Pin the range to a single value (and make
  // sure no stale single-value entry shadows it).
  function setValueFieldRange(field: string, value: string | NumericRange) {
    const range: NumericRange = typeof value === "string"
      ? { min: value, ...defaultRangeFromMin(value) }
      : value;
    setRangeInputs((prev) => ({ ...prev, [field]: range }));
    setSingleInputs((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  // Value-driven features (no *_enabled field) get a UI toggle that drives
  // the value: off -> 0 (remembering the previous sweep range), on -> the
  // remembered range or a sensible default.
  function toggleValueFeature(section: FeatureSectionLike, checked: boolean) {
    const field = valueFieldFor(section);
    if (!field) return;
    if (checked) {
      setValueFieldRange(field, stashedValues[field] ?? VALUE_FEATURE_DEFAULTS[field] ?? "1");
      expandPanel(section.title);
    } else {
      const current: string | NumericRange | undefined = rangeInputs[field] ?? singleInputs[field];
      setStashedValues((prev) => ({ ...prev, [field]: current ?? prev[field] ?? VALUE_FEATURE_DEFAULTS[field] ?? "1" }));
      setValueFieldRange(field, "0");
      collapsePanel(section.title);
      setAddedPanels((prev) => (prev.includes(section.title) ? prev : [...prev, section.title]));
    }
  }

  return (
    <Stack gap="lg">
      <Stack gap={2}>
        <Title order={2}>Automated Simulator</Title>
        <Text c="dimmed" size="sm">
          Configure a Min / Max / Step range for each strategy field and dispatch a sweep to the
          simulation bot. The top results come back ranked by your chosen criterion.
        </Text>
      </Stack>

      <style>{`
        .auto-sim-form .mantine-InputWrapper-label,
        .auto-sim-form .mantine-Switch-label {
          font-size: 1rem;
          font-weight: 650;
          margin-bottom: 6px;
        }
        .auto-sim-form .mantine-InputWrapper-description,
        .auto-sim-form .mantine-Switch-description {
          font-size: 0.9rem;
        }
        .auto-sim-form .feature-title { margin-bottom: 4px; }
        .auto-sim-form .feature-description { margin-bottom: 10px; }
        .auto-sim-form .field-label {
          font-size: 1rem;
          font-weight: 650;
          margin-bottom: 4px;
        }
      `}</style>

      <Grid className="auto-sim-form" gutter="lg" maw="100%" w="100%" mx="auto" align="start">
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Stack gap="md">
            <FeatureColumnToolbar
              available={availableSections}
              onAdd={addSection}
              onCollapseAll={() => setAllCollapsed(visibleSections.map((s) => s.title), true)}
              onExpandAll={() => setAllCollapsed(visibleSections.map((s) => s.title), false)}
            />

            {orderedSections.map(({ section, groupHeader }) => (
              <Fragment key={section.title}>
              {groupHeader ? <FeatureGroupHeader label={groupHeader} /> : null}
              <FeatureCardShell
                section={section}
                collapsed={collapsedPanels.includes(section.title)}
                onToggleCollapsed={() => toggleCollapsed(section.title)}
                onRemove={isSectionRemovable(section) ? () => removeSection(section) : undefined}
                enabledControl={section.enabledKey ? (
                  <Switch
                    checked={singleInputs[section.enabledKey] === "true"}
                    onChange={(e) => updateFeatureToggle(section.title, section.enabledKey!, e.currentTarget.checked)}
                    aria-label={`Enable ${section.title}`}
                  />
                ) : valueFieldFor(section) ? (
                  <Switch
                    checked={isSectionActive(section, singleInputs, { extraActiveKeys: sweptKeys })}
                    onChange={(e) => toggleValueFeature(section, e.currentTarget.checked)}
                    aria-label={`Enable ${section.title}`}
                  />
                ) : null}
              >
                    <Stack gap="md">
                      {section.metaKey === "core" ? (
                        <>
                          <Select
                            searchable
                            label="Config"
                            description="Select base config"
                            placeholder="Select config"
                            data={versionOptions}
                            value={selectedConfigVersionId}
                            onChange={(v) => {
                              // Picking a config version releases any loaded sweep
                              // config so the normal version-snapshot seed resumes.
                              setImportActive(false);
                              setSelectedConfigVersionId(v);
                            }}
                            disabled={versionsQ.isLoading}
                          />
                          <SimpleGrid cols={{ base: 1, sm: 2 }}>
                            <TextInput
                              label="From"
                              type="datetime-local"
                              value={from}
                              onChange={(e) => setFrom(e.currentTarget.value)}
                            />
                            <TextInput
                              label="To"
                              type="datetime-local"
                              value={to}
                              onChange={(e) => setTo(e.currentTarget.value)}
                            />
                          </SimpleGrid>
                          <Select
                            label="Execution Target"
                            description="Choose where the sweep executes. Modal is cloud burst; local runs through sim-bot."
                            value={simTarget}
                            onChange={(v) => setSimTarget((v as SimTarget) ?? "auto")}
                            data={[
                              { value: "auto", label: "Auto" },
                              { value: "bullmq", label: "Local (BullMQ/sim-bot)" },
                              { value: "modal", label: "Modal (cloud)" },
                            ]}
                          />
                          <Text size="sm" c="dimmed">
                            Selected config ID: <Code style={monoStyle}>{selectedConfigVersionId ?? "(none)"}</Code>
                          </Text>
                          <Divider label="Core entry" labelPosition="left" />
                        </>
                      ) : null}
                      {section.title === "Kronos Forecast" ? (
                        <KronosCachePicker
                          marketId={marketId}
                          currentSignature={kronosSignatureFromParams({
                            // Model is editable (singleInputs); the other five
                            // params are non-editable and live in the template.
                            model: String(singleInputs["kronos_model"] ?? configTemplate?.kronos_model ?? "small"),
                            lookback: Number(configTemplate?.kronos_lookback ?? 400),
                            timeframe: Number(configTemplate?.kronos_candle_timeframe_seconds ?? 60),
                            sampleCount: Number(configTemplate?.kronos_sample_count ?? 5),
                            temperature: Number(configTemplate?.kronos_temperature ?? 1),
                            topP: Number(configTemplate?.kronos_top_p ?? 0.9),
                            settled: Boolean(configTemplate?.kronos_settled_precompute_enabled),
                          })}
                          currentDecisionTime={
                            Number(rangeInputs["decision_time_seconds"]?.min ?? configTemplate?.decision_time_seconds) || null
                          }
                          onApply={(info) => {
                            // The five inference params other than the model
                            // are NOT in EDITABLE_KEYS (they were removed from
                            // the sweep surface), so buildSweep copies them
                            // straight from the template. Update the template
                            // itself so the picked signature actually reaches
                            // the sweep's fixed config — otherwise the sweep
                            // runs on the base config's params and the gate
                            // cold-starts to 0 results.
                            setConfigTemplate((prev) => (prev ? {
                              ...prev,
                              kronos_model: info.model,
                              kronos_lookback: info.lookback,
                              kronos_candle_timeframe_seconds: info.timeframe,
                              kronos_sample_count: info.sample_count,
                              kronos_temperature: info.temperature,
                              kronos_top_p: info.top_p,
                              // Adopt the cache's mode so the fixed sweep config
                              // reads the SAME cache (settled vs decide-late).
                              kronos_settled_precompute_enabled: info.settled,
                              decision_time_seconds: info.decision_time_seconds,
                            } : prev));
                            // Model IS editable (a rendered Select backed by
                            // singleInputs), so keep it in sync. Decision time
                            // stays a Min/Max/Step RANGE (rangeInputs) so it can
                            // still be swept; applying a cache just collapses the
                            // range to the picked value (min == max) as a
                            // starting point. Delete any stale singleInputs entry
                            // so the renderer (singleInputs wins over rangeInputs)
                            // doesn't keep showing a single box.
                            setSingleInputs((prev) => {
                              const next: Record<string, string> = { ...prev, kronos_model: info.model };
                              delete next["decision_time_seconds"];
                              return next;
                            });
                            setRangeInputs((prev) => ({
                              ...prev,
                              decision_time_seconds: {
                                min: String(info.decision_time_seconds),
                                max: String(info.decision_time_seconds),
                                step: "1",
                              },
                            }));
                          }}
                        />
                      ) : null}
                      {section.title === "Kronos Forecast" && kronosEnabled ? (
                        <KronosCoveragePanel
                          coverage={kronosCoverage}
                          onUseCacheStart={() => {
                            if (kronosCoverage.requiredFrom) setFrom(toLocalDateTimeInput(kronosCoverage.requiredFrom));
                          }}
                          onUseCacheEnd={() => {
                            if (kronosCoverage.requiredTo) setTo(toLocalDateTimeInput(kronosCoverage.requiredTo));
                          }}
                        />
                      ) : null}
                      {section.keys.length > 0 ? <Divider /> : null}
                      <Stack gap="md">
                        {section.keys.map((key) => {
                          // Hide live_candle_minute entirely when allow_live_candle is off.
                          // When on, fall through to the standard rangeInputs rendering below
                          // so the user gets Min/Max/Step to sweep across values 0-5.
                          if (key === "live_candle_minute" && singleInputs["allow_live_candle"] !== "true") {
                            return null;
                          }
                          // V3 multi-market: candle_timeframe_seconds renders
                          // as a checkbox-per-timeframe (filtered to the active
                          // market's allow-list) instead of Min/Max/Step. Each
                          // checked box becomes one axis value at sweep time.
                          if (key === "candle_timeframe_seconds") {
                            const defaultLabel = (() => {
                              const bucket = candleTimeframeOptions.find((o) => o.seconds === marketBucketSeconds);
                              return `Default (${bucket?.label ?? `${marketBucketSeconds}s`})`;
                            })();
                            const toggle = (val: number, checked: boolean) => {
                              setCandleTfSelected((prev) => {
                                const set = new Set(prev);
                                if (checked) set.add(val); else set.delete(val);
                                // Ensure at least one selection remains so the
                                // sweep never silently turns into "no entry"
                                // for the engine. Re-check "Default" on empty.
                                if (set.size === 0) set.add(0);
                                return [...set].sort((a, b) => a - b);
                              });
                            };
                            return (
                              <div key={key}>
                                <Text className="field-label">{formatFieldLabel(key)}</Text>
                                <Text size="xs" c="dimmed" mb={6}>
                                  Check each candle width to sweep over. Picking more than one creates an axis (multiplies combos by N). "Default" uses the active market's bucket_seconds.
                                </Text>
                                <Stack gap={6}>
                                  <Checkbox
                                    label={defaultLabel}
                                    checked={candleTfSelected.includes(0)}
                                    onChange={(e) => toggle(0, e.currentTarget.checked)}
                                  />
                                  {candleTimeframeOptions.map((opt) => (
                                    <Checkbox
                                      key={opt.seconds}
                                      label={opt.label}
                                      checked={candleTfSelected.includes(opt.seconds)}
                                      onChange={(e) => toggle(opt.seconds, e.currentTarget.checked)}
                                    />
                                  ))}
                                </Stack>
                              </div>
                            );
                          }
                          if (key in singleInputs) {
                            const templateValue = configTemplate?.[key];
                            if (typeof templateValue === "boolean") {
                              // Bypass toggles get an Off / On / Sweep control so
                              // the run can enumerate every on/off combination of
                              // the gates you're willing to let the override bypass.
                              if (SWEEPABLE_BOOL_KEYS.has(key)) {
                                const cur = singleInputs[key] === "true"
                                  ? "true"
                                  : singleInputs[key] === "sweep"
                                    ? "sweep"
                                    : "false";
                                return (
                                  <div key={key}>
                                    <Text className="field-label">{formatFieldLabel(key)}</Text>
                                    {FIELD_DESCRIPTIONS[key] ? (
                                      <Text size="xs" c="dimmed" mb={6}>
                                        {FIELD_DESCRIPTIONS[key]}
                                        {" "}<b>Sweep</b> = test both off and on across all combinations.
                                      </Text>
                                    ) : null}
                                    <SegmentedControl
                                      fullWidth
                                      size="xs"
                                      value={cur}
                                      onChange={(v) => setSingleInputs((prev) => ({ ...prev, [key]: v }))}
                                      data={[
                                        { value: "false", label: "Off" },
                                        { value: "true", label: "On" },
                                        { value: "sweep", label: "Sweep (off & on)" },
                                      ]}
                                    />
                                  </div>
                                );
                              }
                              return (
                                <Switch
                                  key={key}
                                  label={formatFieldLabel(key)}
                                  description={FIELD_DESCRIPTIONS[key]}
                                  checked={singleInputs[key] === "true"}
                                  onChange={(e) => setSingleInputs((prev) => ({
                                    ...prev,
                                    [key]: e.currentTarget.checked ? "true" : "false",
                                  }))}
                                />
                              );
                            }
                            if (key === "kronos_model") {
                              return (
                                <Select
                                  key={key}
                                  label={formatFieldLabel(key)}
                                  description={FIELD_DESCRIPTIONS[key]}
                                  value={singleInputs[key] ?? "small"}
                                  onChange={(v) => setSingleInputs((prev) => ({ ...prev, [key]: v ?? "small" }))}
                                  data={KRONOS_MODEL_OPTIONS}
                                  allowDeselect={false}
                                />
                              );
                            }
                            if (key === "counterpart_confirm_mode") {
                              return (
                                <Select
                                  key={key}
                                  label={formatFieldLabel(key)}
                                  description={FIELD_DESCRIPTIONS[key]}
                                  value={singleInputs[key] ?? "price"}
                                  onChange={(v) => setSingleInputs((prev) => ({ ...prev, [key]: v ?? "price" }))}
                                  data={[
                                    { value: "price", label: "Counterpart underlying move vs its open" },
                                    { value: "favorite", label: "Counterpart favourite side must match" },
                                  ]}
                                  allowDeselect={false}
                                />
                              );
                            }
                            if (key === "momentum_sell_window_type") {
                              return (
                                <Select
                                  key={key}
                                  label={formatFieldLabel(key)}
                                  description={FIELD_DESCRIPTIONS[key]}
                                  value={singleInputs[key] ?? "number"}
                                  onChange={(v) => setSingleInputs((prev) => ({ ...prev, [key]: v ?? "number" }))}
                                  data={[
                                    { value: "number", label: "Fixed seconds" },
                                    { value: "percentage", label: "% of remaining window at fill" },
                                  ]}
                                  allowDeselect={false}
                                />
                              );
                            }
                            if (key === "momentum_sell_sl_threshold_type" || key === "momentum_sell_tp_threshold_type") {
                              const isSl = key === "momentum_sell_sl_threshold_type";
                              return (
                                <Select
                                  key={key}
                                  label={formatFieldLabel(key)}
                                  description={FIELD_DESCRIPTIONS[key]}
                                  value={singleInputs[key] ?? "points"}
                                  onChange={(v) => setSingleInputs((prev) => ({ ...prev, [key]: v ?? "points" }))}
                                  data={[
                                    { value: "points", label: isSl ? "Odds points below fill" : "Odds points above fill" },
                                    { value: "percentage", label: isSl ? "% below fill ask" : "% above fill ask" },
                                  ]}
                                  allowDeselect={false}
                                />
                              );
                            }
                            return (
                              <TextInput
                                key={key}
                                label={formatFieldLabel(key)}
                                description={FIELD_DESCRIPTIONS[key]}
                                value={singleInputs[key] ?? ""}
                                onChange={(e) => setSingleInputs((prev) => ({ ...prev, [key]: e.currentTarget.value }))}
                                styles={{ input: monoStyle }}
                              />
                            );
                          }
                          if (key in rangeInputs) {
                            const range = rangeInputs[key]!;
                            // The live-candle index can't exceed the number of
                            // candles in the window. Compute the ceiling from the
                            // selected timeframe(s) (coarsest = fewest candles)
                            // and surface it so the operator knows values above it
                            // are clamped out of the sweep.
                            const liveCandleCeiling = key === "live_candle_minute"
                              ? Math.max(
                                  1,
                                  Math.min(
                                    ...(candleTfSelected.length > 0 ? candleTfSelected : [0])
                                      .map((s) => (s > 0 ? s : Math.max(60, marketBucketSeconds)))
                                      .map((tf) => Math.floor(marketWindowSeconds / Math.max(1, tf))),
                                  ),
                                )
                              : null;
                            return (
                              <div key={key}>
                                <Text className="field-label">{formatFieldLabel(key)}</Text>
                                {FIELD_DESCRIPTIONS[key] ? (
                                  <Text size="xs" c="dimmed" mb={6}>{FIELD_DESCRIPTIONS[key]}</Text>
                                ) : null}
                                {liveCandleCeiling != null ? (
                                  <Text size="xs" c="dimmed" mb={6}>
                                    Max valid index for the selected timeframe is <b>{liveCandleCeiling}</b> ({liveCandleCeiling} candles in the window). Higher values are clamped out of the sweep.
                                  </Text>
                                ) : null}
                                <SimpleGrid cols={3} spacing="xs">
                                  <TextInput
                                    placeholder="Min"
                                    aria-label={`${key} min`}
                                    value={range.min}
                                    onChange={(e) => setRangeInputs((prev) => {
                                      const prevRange = prev[key]!;
                                      const nextMin = e.currentTarget.value;
                                      const prevDefaults = defaultRangeFromMin(prevRange.min);
                                      const nextDefaults = defaultRangeFromMin(nextMin);
                                      return {
                                        ...prev,
                                        [key]: {
                                          ...prevRange,
                                          min: nextMin,
                                          max: prevRange.max === prevDefaults.max ? nextDefaults.max : prevRange.max,
                                          step: prevRange.step === prevDefaults.step ? nextDefaults.step : prevRange.step,
                                        },
                                      };
                                    })}
                                    styles={{ input: monoStyle }}
                                  />
                                  <TextInput
                                    placeholder="Max"
                                    aria-label={`${key} max`}
                                    value={range.max}
                                    onChange={(e) => setRangeInputs((prev) => ({
                                      ...prev,
                                      [key]: { ...prev[key]!, max: e.currentTarget.value },
                                    }))}
                                    styles={{ input: monoStyle }}
                                  />
                                  <TextInput
                                    placeholder="Step"
                                    aria-label={`${key} step`}
                                    value={range.step}
                                    onChange={(e) => setRangeInputs((prev) => ({
                                      ...prev,
                                      [key]: { ...prev[key]!, step: e.currentTarget.value },
                                    }))}
                                    styles={{ input: monoStyle }}
                                  />
                                </SimpleGrid>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </Stack>
                    </Stack>
              </FeatureCardShell>
              </Fragment>
            ))}

            <Card withBorder>
              <Stack gap="sm">
                <div>
                  <Text className="feature-title" size="lg" fw={700}>Results Settings</Text>
                  <Text className="feature-description" size="sm" c="dimmed">
                    Tell the simulation bot how to rank combinations and how many top finalists to return.
                  </Text>
                </div>
                <Divider />
                <Select
                  label="Sort By"
                  data={SORT_BY_OPTIONS}
                  value={sortBy}
                  onChange={(v) => setSortBy((v as SortBy) ?? "pnl")}
                  allowDeselect={false}
                />
                <NumberInput
                  label="Top Results To Return"
                  description="Number of best combinations to show (default 10)."
                  min={1}
                  max={500}
                  value={topResults}
                  onChange={(v) => setTopResults(typeof v === "number" ? v : Number(v) || 10)}
                />
                <NumberInput
                  label="Minimum Wins"
                  description="Only include combinations whose total wins meet this threshold. Useful with Least Losses so 0-trade or 1-win combos are excluded."
                  min={0}
                  max={10000}
                  value={minWins}
                  onChange={(v) => setMinWins(typeof v === "number" ? v : Number(v) || 0)}
                />
                <NumberInput
                  label="Minimum Win Rate (%)"
                  description="Only include combinations whose win rate is at or above this percentage (0–100). Set e.g. 90 with Sort By = PnL to rank only 90%+ win-rate configs."
                  min={0}
                  max={100}
                  step={0.5}
                  decimalScale={2}
                  value={minWinRate}
                  onChange={(v) => setMinWinRate(typeof v === "number" ? v : Number(v) || 0)}
                />
                <NumberInput
                  label="Maximum Losses"
                  description="Only include combinations whose total losses (losses + stop-losses) stay at or below this cap. Leave empty to disable."
                  min={0}
                  max={100000}
                  value={maxLosses}
                  onChange={(v) => {
                    if (v === "" || v === null || v === undefined) {
                      setMaxLosses("");
                      return;
                    }
                    const n = typeof v === "number" ? v : Number(v);
                    setMaxLosses(Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : "");
                  }}
                  placeholder="No cap"
                />
                <Switch
                  label="Best Result Per Loss Count"
                  description="Collapse the finalists to the single best combination for each distinct total-losses count. Within each loss bucket only the highest win amount (per Sort By) is returned — e.g. results with 3 losses and 5 losses return just two combos, the best of each."
                  checked={bestPerLossCount}
                  onChange={(e) => setBestPerLossCount(e.currentTarget.checked)}
                />
                <Switch
                  label="Subtract Historical No-Fills From Results"
                  description="Exclude historically no-fill windows from aggregate wins, losses, win rate, and P/L while still reporting no-fill counts."
                  checked={subtractNoFillsFromResults}
                  onChange={(e) => setSubtractNoFillsFromResults(e.currentTarget.checked)}
                />
                {/* exclude_stale_odds: drop windows whose Polymarket odds never moved */}
                <Switch
                  label="Exclude stale windows (odds never moved)"
                  description="Drop windows whose Polymarket odds stayed pinned all window (maintenance / market-maker-absent) so they don't skew results."
                  checked={excludeStaleOdds}
                  onChange={(e) => setExcludeStaleOdds(e.currentTarget.checked)}
                />
                {simTarget !== "modal" && (
                  <>
                    <Divider label="Scheduling & Batching (local runs only)" labelPosition="center" />
                    <Switch
                      label="Start now"
                      description="Turn off to schedule this run to begin at a specific date & time."
                      checked={scheduleNow}
                      onChange={(e) => setScheduleNow(e.currentTarget.checked)}
                    />
                    {!scheduleNow && (
                      <TextInput
                        label="Scheduled start"
                        type="datetime-local"
                        leftSection={<IconClock size={14} />}
                        value={scheduledStart}
                        onChange={(e) => setScheduledStart(e.currentTarget.value)}
                      />
                    )}
                    <NumberInput
                      label="Batch Size (combos per sub-run)"
                      description="0 = run the entire job at once. Otherwise the sweep is split into sequential sub-runs of this many combinations each, so the machine only evaluates one batch at a time. You can pause between batches to free up CPU/RAM and resume later — the final results merge every batch's top combos."
                      min={0}
                      max={1_000_000_000}
                      step={100000}
                      value={batchSize}
                      onChange={(v) => setBatchSize(typeof v === "number" ? v : Number(v) || 0)}
                    />
                    {batchSize > 0 && comboCount > 0 && (
                      <Text size="xs" c="dimmed">
                        <Code style={monoStyle}>{comboCount.toLocaleString()}</Code> combos will run as{" "}
                        <Code style={monoStyle}>{Math.max(1, Math.ceil(comboCount / batchSize)).toLocaleString()}</Code>{" "}
                        sequential batches of up to <Code style={monoStyle}>{batchSize.toLocaleString()}</Code> combos each.
                      </Text>
                    )}
                    <Divider label="Machine Resources (local runs only)" labelPosition="center" />
                    <SimpleGrid cols={2} spacing="sm">
                      <NumberInput
                        label="Max CPU (%)"
                        description="Upper bound on CPU parallelism. Blank = all cores but one (kept free for the trading bot sharing this machine), subject to memory limits. Set 100 to use every core."
                        min={1}
                        max={100}
                        step={5}
                        placeholder="auto"
                        value={cpuPercent}
                        onChange={(v) => setCpuPercent(typeof v === "number" ? v : "")}
                      />
                      <NumberInput
                        label="Max RAM (%)"
                        description="Memory budget for the local sweep. Blank = 60%, reduced when available memory is low; at least 10% is reserved for the system."
                        min={1}
                        max={90}
                        step={5}
                        placeholder="60"
                        value={ramPercent}
                        onChange={(v) => setRamPercent(typeof v === "number" ? v : "")}
                      />
                    </SimpleGrid>
                    <Text size="xs" c="dimmed">
                      Worker count is adjusted after loading market data. During evaluation, memory
                      checks reclaim caches first, then stop the run and preserve partial results if
                      pressure remains. These are periodic safeguards, not an OS-enforced hard limit.
                    </Text>
                  </>
                )}
                <Divider />
                <Switch
                  label="Fast Screen (successive halving)"
                  description="Prune weak combos on a small window subset before scoring survivors on all windows. Large speedup on huge combo spaces; the final ranking is exact for survivors, but a strong combo that looks weak on the initial subset can be pruned early. Off by default."
                  checked={fastScreenEnabled}
                  onChange={(e) => setFastScreenEnabled(e.currentTarget.checked)}
                />
                {fastScreenEnabled && (
                  <>
                    <NumberInput
                      label="Fast Screen: Pruning Factor (eta)"
                      description="Each rung keeps the best 1/eta of candidates and widens the window subset by ~eta. Higher = more aggressive pruning = faster but riskier. Minimum 2 (default 3)."
                      min={2}
                      max={10}
                      value={fastScreenEta}
                      onChange={(v) => setFastScreenEta(typeof v === "number" ? v : Number(v) || 3)}
                    />
                    <NumberInput
                      label="Fast Screen: Initial Windows"
                      description="Window count for the first rung. 0 = auto (~total windows / eta^2, giving roughly three rungs)."
                      min={0}
                      max={100000}
                      value={fastScreenMinWindows}
                      onChange={(v) => setFastScreenMinWindows(typeof v === "number" ? v : Number(v) || 0)}
                    />
                    <NumberInput
                      label="Fast Screen: Max Survivors per Rung (memory cap)"
                      description="Absolute cap on how many combos any early rung promotes. 0 = unlimited. On huge combo spaces the pool is otherwise 1/eta of ALL combos (millions), and the parent holds every survivor in RAM — the cause of out-of-memory crashes. Capping prunes more aggressively but bounds memory. Try 500,000–2,000,000 for 10M+ combo runs. The Max RAM % control below also derives a cap automatically."
                      min={0}
                      max={100000000}
                      step={100000}
                      value={fastScreenMaxKeep}
                      onChange={(v) => setFastScreenMaxKeep(typeof v === "number" ? v : Number(v) || 0)}
                    />
                    <Switch
                      label="Fast Screen: Dedup early rungs (fast)"
                      description="On (default) = fast: on early rungs, combos with identical outcomes on the small window subset are merged into one survivor, so far fewer combos advance to later rungs. Turn OFF for a more thorough screen that carries the full survivor pool forward each rung — much slower on large grids. The final rung always dedups."
                      checked={fastScreenEarlyDedup}
                      onChange={(e) => setFastScreenEarlyDedup(e.currentTarget.checked)}
                    />
                    <FastScreenRecommendationsTable
                      comboCount={comboCount}
                      onApply={(eta, windows) => {
                        setFastScreenEta(eta);
                        setFastScreenMinWindows(windows);
                      }}
                    />
                  </>
                )}
              </Stack>
            </Card>

            {kronosCoverage.blocking && !kronosCoverage.loading ? (
              <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />} title="Run blocked: Kronos cache coverage incomplete">
                <Text size="sm">
                  {coverageBlockSummary}
                  {" "}See the checklist in the <b>Kronos Forecast</b> section above.
                </Text>
              </Alert>
            ) : null}

            <Group justify="space-between" align="center">
              <Text size="sm" c="dimmed">
                Combinations to evaluate:{" "}
                <Code style={monoStyle}>{comboCount.toLocaleString()}</Code>
              </Text>
              <Group gap="xs">
                <Button
                  variant="light"
                  leftSection={<IconDownload size={14} />}
                  onClick={exportModalConfig}
                  disabled={!sweepPayload || versionsQ.isLoading || cancelMut.isPending || simTarget !== "modal"}
                >
                  Export Modal Config
                </Button>
                <Button
                  onClick={() => runMut.mutate()}
                  loading={runMut.isPending}
                  disabled={
                    !sweepPayload
                    || versionsQ.isLoading
                    || cancelMut.isPending
                    || (simTarget === "modal" && modalReadinessQ.data?.ready === false)
                    || kronosCoverage.blocking
                  }
                >
                  Run Automated Simulations
                </Button>
              </Group>
            </Group>

            <Card withBorder>
              <Stack gap="sm">
                <div>
                  <Text className="feature-title" size="lg" fw={700}>Modal Export</Text>
                  <Text className="feature-description" size="sm" c="dimmed">
                    Export the current form as a Modal-ready sweep config JSON.
                  </Text>
                </div>
                <Divider />
                <NumberInput
                  label="Modal Batches"
                  description="How many slices/containers to use for modal runner dispatch."
                  min={1}
                  max={10000}
                  value={modalBatches}
                  onChange={(v) => setModalBatches(typeof v === "number" ? v : Number(v) || 25)}
                />
                <TextInput
                  label="Modal Artifacts Directory"
                  description="Directory for batch_result_*.json files used by normal runs and resume-only aggregation."
                  value={modalArtifactsDir}
                  onChange={(e) => setModalArtifactsDir(e.currentTarget.value)}
                />
                {simTarget === "modal" ? (
                  <Paper withBorder p="sm">
                    <Stack gap={6}>
                      <Group justify="space-between" align="center">
                        <Text fw={600} size="sm">Modal Readiness</Text>
                        <Badge color={modalReadinessQ.data?.ready ? "green" : "orange"}>
                          {modalReadinessQ.data?.ready ? "ready" : modalReadinessQ.isLoading ? "checking" : "not ready"}
                        </Badge>
                      </Group>
                      <Text size="xs" c="dimmed">
                        Command: <Code style={monoStyle}>{modalReadinessQ.data?.command ?? "..."}</Code>
                      </Text>
                      {modalReadinessQ.data && !modalReadinessQ.data.ready ? (
                        <Stack gap={2}>
                          {modalReadinessQ.data.errors.map((err) => (
                            <Text key={err} size="xs" c="red">• {err}</Text>
                          ))}
                        </Stack>
                      ) : null}
                    </Stack>
                  </Paper>
                ) : null}
                <Text size="xs" c="dimmed">
                  Export writes a raw sweep form JSON for direct use with
                  `modal run ../simulation-bot/src/sim_bot/modal/runner.py --form &lt;file&gt;`.
                </Text>
                <Group justify="flex-end">
                  <TextInput
                    placeholder="Optional source run ID"
                    value={modalResumeFromRunId}
                    onChange={(e) => setModalResumeFromRunId(e.currentTarget.value)}
                    size="xs"
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="default"
                    onClick={() => resumeMut.mutate()}
                    loading={resumeMut.isPending}
                    disabled={
                      !sweepPayload
                      || simTarget !== "modal"
                      || !modalArtifactsDir.trim()
                    }
                  >
                    Resume From Artifacts
                  </Button>
                  <Button
                    variant="light"
                    leftSection={<IconDownload size={14} />}
                    onClick={exportModalConfig}
                    disabled={!sweepPayload || versionsQ.isLoading || cancelMut.isPending || simTarget !== "modal"}
                  >
                    Download Modal Config JSON
                  </Button>
                </Group>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Stack gap="md">
            <Card withBorder>
              <Stack gap="sm">
                <Group justify="space-between" align="center">
                  <Title order={5}>Active Simulation Queue</Title>
                  {activeQ.isLoading ? <Badge color="gray">loading</Badge> : null}
                </Group>

                {activeRuns.length === 0 ? (
                  <Text size="sm" c="dimmed">No queued or running automated simulations.</Text>
                ) : (
                  <Stack gap="xs">
                    {activeRuns.map((run) => {
                      const isQueued = run.status === "queued";
                      const isRunning = run.status === "running";
                      const isSelected = runId === run.run_id;
                      return (
                        <Paper
                          key={run.run_id}
                          withBorder
                          p="sm"
                          style={{
                            borderColor: isSelected ? "var(--mantine-color-blue-5)" : undefined,
                            borderWidth: isSelected ? 2 : 1,
                          }}
                        >
                          <Stack gap={6}>
                            <Group justify="space-between" align="center">
                              <Group gap="xs" align="center">
                                <Badge color={statusColor(run.status)}>{run.status}</Badge>
                                <Code style={monoStyle}>{run.run_id}</Code>
                              </Group>
                              <Button
                                size="xs"
                                variant={isSelected ? "filled" : "light"}
                                onClick={() => setRunId(run.run_id)}
                              >
                                {isSelected ? "Viewing" : "View"}
                              </Button>
                            </Group>

                            <Progress value={(run.progress ?? 0) * 100} color={statusColor(run.status)} />
                            <Text size="xs" c="dimmed">
                              Created: {new Date(run.created_at).toLocaleString()}
                            </Text>
                            {run.is_batch_parent ? (
                              <Group gap="xs" align="center">
                                <Badge
                                  size="sm"
                                  variant="light"
                                  color="grape"
                                  leftSection={<IconLayersSubtract size={11} />}
                                >
                                  Batch {(run.batch_completed ?? 0)}/{run.batch_total ?? 0}
                                </Badge>
                                {run.paused ? <Badge size="sm" color="yellow">paused</Badge> : null}
                                {run.scheduled_start_at && Date.parse(run.scheduled_start_at) > Date.now() ? (
                                  <Badge size="sm" color="cyan" variant="light" leftSection={<IconClock size={11} />}>
                                    starts {new Date(run.scheduled_start_at).toLocaleString()}
                                  </Badge>
                                ) : null}
                              </Group>
                            ) : null}

                            <Group gap="xs">
                              {run.is_batch_parent && (isQueued || isRunning) ? (
                                run.paused ? (
                                  <Button
                                    size="xs"
                                    variant="light"
                                    color="green"
                                    leftSection={<IconPlayerPlay size={13} />}
                                    onClick={() => resumeMut2.mutate(run.run_id)}
                                    loading={resumeMut2.isPending}
                                    disabled={cancelMut.isPending || deleteQueuedMut.isPending}
                                  >
                                    Resume
                                  </Button>
                                ) : (
                                  <Button
                                    size="xs"
                                    variant="light"
                                    color="yellow"
                                    leftSection={<IconPlayerPause size={13} />}
                                    onClick={() => pauseMut.mutate(run.run_id)}
                                    loading={pauseMut.isPending}
                                    disabled={cancelMut.isPending || deleteQueuedMut.isPending}
                                  >
                                    Pause
                                  </Button>
                                )
                              ) : null}

                              {(isQueued || isRunning) ? (
                                <Button
                                  size="xs"
                                  color="orange"
                                  variant="light"
                                  leftSection={<IconX size={13} />}
                                  onClick={() => {
                                    if (window.confirm(`Stop run ${run.run_id}?`)) {
                                      cancelMut.mutate(run.run_id);
                                    }
                                  }}
                                  loading={cancelMut.isPending}
                                  disabled={deleteQueuedMut.isPending}
                                >
                                  Stop
                                </Button>
                              ) : null}

                              {isQueued ? (
                                <Button
                                  size="xs"
                                  color="red"
                                  variant="light"
                                  onClick={() => {
                                    if (window.confirm(`Delete queued run ${run.run_id}?`)) {
                                      deleteQueuedMut.mutate(run.run_id);
                                    }
                                  }}
                                  loading={deleteQueuedMut.isPending}
                                  disabled={cancelMut.isPending}
                                >
                                  Delete
                                </Button>
                              ) : null}
                            </Group>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </Card>

            {modalSavedRuns.length > 0 ? (
              <Card withBorder>
                <Stack gap="sm">
                  <Group justify="space-between" align="center">
                    <Title order={5}>Saved Modal Results</Title>
                    <Badge color="violet" variant="light">{modalSavedRuns.length}</Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    Existing modal sweep results found in the database.
                  </Text>

                  <Stack gap="xs">
                    {modalSavedRuns.slice(0, 20).map((run) => {
                      const summaryBlob = (run.result_summary ?? {}) as Record<string, unknown>;
                      const bestPnl = summaryBlob.best_pnl_usdc == null
                        ? null
                        : Number(summaryBlob.best_pnl_usdc);
                      const combosEval = summaryBlob.combos_evaluated == null
                        ? null
                        : Number(summaryBlob.combos_evaluated);
                      const combosTotalLocal = summaryBlob.combo_count_total == null
                        ? null
                        : Number(summaryBlob.combo_count_total);
                      const isSelected = runId === run.run_id;
                      return (
                        <Paper
                          key={run.run_id}
                          withBorder
                          p="sm"
                          style={{
                            borderColor: isSelected ? "var(--mantine-color-blue-5)" : undefined,
                            borderWidth: isSelected ? 2 : 1,
                          }}
                        >
                          <Group justify="space-between" align="center" wrap="nowrap">
                            <Stack gap={2}>
                              <Group gap="xs" align="center">
                                <Badge color={statusColor(run.status)}>{run.status}</Badge>
                                <Code style={monoStyle}>{run.run_id}</Code>
                              </Group>
                              <Text size="xs" c="dimmed">
                                Finished: {run.finished_at ? new Date(run.finished_at).toLocaleString() : "—"}
                                {combosEval != null && combosTotalLocal != null
                                  ? ` · ${Math.max(0, combosEval).toLocaleString()}/${Math.max(0, combosTotalLocal).toLocaleString()} combos`
                                  : ""}
                                {bestPnl != null ? ` · Best P/L ${bestPnl >= 0 ? "+" : ""}$${bestPnl.toFixed(2)}` : ""}
                              </Text>
                            </Stack>

                            <Button
                              size="xs"
                              variant={isSelected ? "filled" : "light"}
                              onClick={() => setRunId(run.run_id)}
                            >
                              {isSelected ? "Viewing" : "View Results"}
                            </Button>
                          </Group>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Stack>
              </Card>
            ) : null}

            <Card withBorder>
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Title order={4}>Top {topResults} Results</Title>
                <Group gap="xs">
                  {runQ.data ? (
                    <Badge color={statusColor(runQ.data.status)}>{runQ.data.status}</Badge>
                  ) : null}
                  {runQ.data?.is_batch_parent ? (
                    <Badge color="grape" variant="light" leftSection={<IconLayersSubtract size={12} />}>
                      Batch {(runQ.data.batch_completed ?? 0)}/{runQ.data.batch_total ?? 0}
                    </Badge>
                  ) : null}
                  {runQ.data?.is_batch_parent && (runQ.data.status === "queued" || runQ.data.status === "running") ? (
                    runQ.data.paused ? (
                      <Button
                        size="xs"
                        color="green"
                        variant="light"
                        leftSection={<IconPlayerPlay size={14} />}
                        onClick={() => resumeMut2.mutate(runQ.data!.run_id)}
                        loading={resumeMut2.isPending}
                      >
                        Resume
                      </Button>
                    ) : (
                      <Button
                        size="xs"
                        color="yellow"
                        variant="light"
                        leftSection={<IconPlayerPause size={14} />}
                        onClick={() => pauseMut.mutate(runQ.data!.run_id)}
                        loading={pauseMut.isPending}
                      >
                        Pause
                      </Button>
                    )
                  ) : null}
                  {runQ.data && (runQ.data.status === "queued" || runQ.data.status === "running") ? (
                    <Button
                      size="xs"
                      color="orange"
                      variant="light"
                      leftSection={<IconX size={14} />}
                      onClick={() => {
                        if (window.confirm("Stop this simulation run now?")) {
                          cancelMut.mutate(runQ.data.run_id);
                        }
                      }}
                      loading={cancelMut.isPending}
                      disabled={runMut.isPending || discardMut.isPending}
                    >
                      Stop Simulation
                    </Button>
                  ) : null}
                  {runQ.data && FINAL_STATUSES.has(runQ.data.status) ? (
                    <Button
                      size="xs"
                      color="red"
                      variant="light"
                      leftSection={<IconX size={14} />}
                      onClick={() => {
                        if (window.confirm("Delete this run? Its results will be permanently removed.")) {
                          discardMut.mutate();
                        }
                      }}
                      loading={discardMut.isPending}
                      disabled={cancelMut.isPending}
                    >
                      Delete
                    </Button>
                  ) : null}
                </Group>
              </Group>

              {!runId ? <EmptyState label="No automated run started yet." /> : null}
              {runQ.isLoading ? <LoadingState label="Polling simulation run..." /> : null}
              {runQ.isError ? <ErrorState error={runQ.error as Error} onRetry={() => runQ.refetch()} /> : null}

              {runQ.data ? (
                <Stack gap="sm">
                  <Text size="sm">Run ID: <Code style={monoStyle}>{runQ.data.run_id}</Code></Text>
                  <Badge color={noFillSubtractionEnabled ? "teal" : "gray"} variant="light">
                    No-Fill Subtraction: {noFillSubtractionEnabled ? "ON" : "OFF"}
                  </Badge>
                  <Badge color={staleOddsExcludedEnabled ? "teal" : "gray"} variant="light">
                    Stale Excluded: {staleOddsExcludedEnabled ? "ON" : "OFF"}
                  </Badge>
                  {(combosEvaluated != null && combosTotal != null && combosTotal > 0) ? (
                    <Text size="sm" c="dimmed">
                      Simulation Progress: <Code style={monoStyle}>{Math.max(0, combosEvaluated).toLocaleString()}</Code>
                      {" / "}
                      <Code style={monoStyle}>{Math.max(0, combosTotal).toLocaleString()}</Code>
                    </Text>
                  ) : null}
                  <Progress value={(runQ.data.progress ?? 0) * 100} color={statusColor(runQ.data.status)} />
                  {runQ.data.error ? <Text size="sm" c="red">{runQ.data.error}</Text> : null}
                  {runIssue ? (
                    <Alert
                      color={runQ.data.status === "cancelled" ? "gray" : "red"}
                      variant="light"
                      title={runIssue.title}
                      icon={<IconAlertTriangle size={16} />}
                    >
                      <Stack gap={4}>
                        <Text size="sm">{runIssue.message}</Text>
                        <Text size="sm" c="dimmed">Next step: {runIssue.nextStep}</Text>
                        {runIssue.evidence.length > 0 ? (
                          <Stack gap={2}>
                            {runIssue.evidence.map((line, idx) => (
                              <Text key={`${line}-${idx}`} size="xs" style={monoStyle} c="dimmed">{line}</Text>
                            ))}
                          </Stack>
                        ) : null}
                      </Stack>
                    </Alert>
                  ) : null}

                  {summary ? (
                    <>
                      <SimpleGrid cols={{ base: 2, md: 4 }}>
                        <Stat label="Combos Evaluated" value={String(summary.combos_evaluated ?? "—")} />
                        <Stat label="Combos With Trades" value={String(summary.combos_valid ?? "—")} />
                        <Stat label="Finalists" value={String(summary.top_k_global ?? finalists.length)} />
                        <Stat
                          label="Top Take Profits"
                          value={topFinalist ? String(topFinalist.take_profits) : "—"}
                          subValue={topFinalist ? `$${topFinalist.take_profit_total_won_usdc.toFixed(2)} won` : undefined}
                          accent="green"
                          subAccent="green"
                        />
                        <Stat
                          label="Top Stop Losses"
                          value={topFinalist ? String(topFinalist.stop_losses) : "—"}
                          subValue={topFinalist ? `$${topFinalist.stop_loss_total_lost_usdc.toFixed(2)} lost` : undefined}
                          accent="red"
                          subAccent="red"
                        />
                        <Stat
                          label="Top Momentum Profit Sells"
                          value={topFinalist ? String(topFinalist.momentum_sell_profit_count ?? 0) : "—"}
                          subValue={topFinalist ? `$${(topFinalist.momentum_sell_profit_pnl_usdc ?? 0).toFixed(2)} won` : undefined}
                          accent="green"
                          subAccent="green"
                        />
                        <Stat
                          label="Top Momentum Loss Sells"
                          value={topFinalist ? String(topFinalist.momentum_sell_loss_count ?? 0) : "—"}
                          subValue={topFinalist ? `$${(topFinalist.momentum_sell_loss_usdc ?? 0).toFixed(2)} lost` : undefined}
                          accent="red"
                          subAccent="red"
                        />
                        <Stat
                          label="Top No-Fill Wins / Losses"
                          value={topFinalist ? `${topFinalist.no_fill_wins} / ${topFinalist.no_fill_losses}` : "—"}
                        />
                        <Stat
                          label="Best P/L"
                          value={summary.best_pnl_usdc == null ? "—" : `$${Number(summary.best_pnl_usdc).toFixed(2)}`}
                          accent={Number(summary.best_pnl_usdc ?? 0) >= 0 ? "green" : "red"}
                        />
                      </SimpleGrid>

                      <Divider />

                      {finalists.length === 0 ? (
                        <EmptyState
                          label={runQ.data.status === "succeeded"
                            ? "Run finished, but no finalists were returned."
                            : "No finalists yet — waiting for the sweep to complete."}
                        />
                      ) : (
                        <ScrollArea h={600} type="auto" offsetScrollbars>
                          <Table striped highlightOnHover withTableBorder stickyHeader>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>#</Table.Th>
                                <Table.Th>Settings</Table.Th>
                                <Table.Th>Wins</Table.Th>
                                <Table.Th>Losses (Incl. SL)</Table.Th>
                                <Table.Th>Stop Losses</Table.Th>
                                <Table.Th>No-Fill W / L</Table.Th>
                                <Table.Th>Win Rate</Table.Th>
                                <Table.Th>P/L</Table.Th>
                                <Table.Th>Fees</Table.Th>
                                <Table.Th>Combination Variations</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {finalists.map((row, idx) => (
                                <Table.Tr key={`${row.combo_index}-${idx}`}>
                                  <Table.Td>{idx + 1}</Table.Td>
                                  <Table.Td>
                                    <Anchor
                                      component="button"
                                      type="button"
                                      size="sm"
                                      onClick={() => setSettingsModalRow(row)}
                                    >
                                      View Settings
                                    </Anchor>
                                  </Table.Td>
                                  <Table.Td>{row.wins}</Table.Td>
                                  <Table.Td>{row.total_losses}</Table.Td>
                                  <Table.Td>
                                    <Text fw={800} style={monoStyle}>{row.stop_losses}</Text>
                                    <Text size="xs" c="red" style={monoStyle}>${row.stop_loss_total_lost_usdc.toFixed(2)} lost</Text>
                                  </Table.Td>
                                  <Table.Td>{row.no_fill_wins} / {row.no_fill_losses}</Table.Td>
                                  <Table.Td>{(row.win_rate * 100).toFixed(1)}%</Table.Td>
                                  <Table.Td style={{ color: row.est_pnl_usdc >= 0 ? "var(--mantine-color-green-6)" : "var(--mantine-color-red-6)" }}>
                                    {row.est_pnl_usdc >= 0 ? "+" : ""}${row.est_pnl_usdc.toFixed(2)}
                                  </Table.Td>
                                  <Table.Td>${row.fees_usdc.toFixed(2)}</Table.Td>
                                  <Table.Td>{row.combination_variations}</Table.Td>
                                </Table.Tr>
                              ))}
                            </Table.Tbody>
                          </Table>
                        </ScrollArea>
                      )}
                    </>
                  ) : null}

                  <Divider />
                  <Stack gap="xs">
                    <Text fw={600}>Simulation Log</Text>
                    {runLogs.length === 0 ? (
                      <Text size="sm" c="dimmed">No log lines yet.</Text>
                    ) : (
                      <ScrollArea h={220} type="auto" offsetScrollbars>
                        <Stack gap={4}>
                          {runLogs.map((row, idx) => (
                            <Text
                              key={`${row.ts}-${idx}`}
                              size="xs"
                              style={{
                                ...monoStyle,
                                color: row.level === "error"
                                  ? "var(--mantine-color-red-6)"
                                  : row.level === "warn"
                                    ? "var(--mantine-color-yellow-7)"
                                    : "inherit",
                              }}
                            >
                              {row.ts ? `[${row.ts}] ` : ""}
                              {row.level.toUpperCase()} {row.message}
                            </Text>
                          ))}
                        </Stack>
                      </ScrollArea>
                    )}
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>

      {activeCfgQ.isLoading || versionsQ.isLoading ? <LoadingState label="Loading configs..." /> : null}
      {activeCfgQ.isError ? <ErrorState error={activeCfgQ.error as Error} onRetry={() => activeCfgQ.refetch()} /> : null}
      {versionsQ.isError ? <ErrorState error={versionsQ.error as Error} onRetry={() => versionsQ.refetch()} /> : null}

      <Modal
        opened={settingsModalRow != null}
        onClose={() => setSettingsModalRow(null)}
        title={settingsModalRow ? `Combo #${settingsModalRow.combo_index} Settings` : "Settings"}
        size="lg"
      >
        {settingsModalRow ? (
          <Stack gap="sm">
            <Anchor
              href={buildManualSimulatorImportUrl({
                settings: settingsModalRow.settings,
                from,
                to,
              })}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
            >
              Use these settings in Manual Simulator
            </Anchor>
            <Paper withBorder p="sm" radius="md">
              <ScrollArea h={420} type="auto" offsetScrollbars>
                <Code block style={{ ...monoStyle, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {JSON.stringify(settingsModalRow.settings, null, 2)}
                </Code>
              </ScrollArea>
            </Paper>
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
}

function Stat({
  label,
  value,
  accent,
  subValue,
  subAccent,
}: {
  label: string;
  value: string;
  accent?: "green" | "red";
  subValue?: string;
  subAccent?: "green" | "red";
}) {
  const color = accent === "green"
    ? "var(--mantine-color-green-6)"
    : accent === "red"
      ? "var(--mantine-color-red-6)"
      : undefined;
  const subColor = subAccent === "green"
    ? "var(--mantine-color-green-6)"
    : subAccent === "red"
      ? "var(--mantine-color-red-6)"
      : "var(--mantine-color-dimmed)";
  return (
    <Paper withBorder radius="md" p="sm">
      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>{label}</Text>
      <Text fw={800} style={monoStyle} c={color}>{value}</Text>
      {subValue ? <Text size="xs" c={subColor} style={monoStyle}>{subValue}</Text> : null}
    </Paper>
  );
}

